"use client";
import React, { useRef, useState, useEffect, useCallback } from "react";
import { QuotationBlock, TourInfo, TransportationInfo, RestaurantInfo, AccommodationInfo, FlightInfo, FlightEvent, AccommodationEvent } from "@/components/quotation/types";
import { format, parseISO } from "date-fns";
import { ptBR, Locale } from "date-fns/locale";
import { Calendar, Clock, MapPin, ChevronRight, ChevronLeft, Image, Maximize2, X, ExternalLink, Plane, PlaneTakeoff, ArrowRight, Info, Bed } from "lucide-react";
import { loadGoogleMapsApi, isGoogleMapsLoaded } from "@/lib/google-maps-loader";
import { formatSafeDate } from "@/lib/date-utils";
import { createGoogleMapsUrl, openInGoogleMaps } from "@/lib/google-maps-utils";
import { SimpleFlightEventItem } from "@/components/quotation/SimpleFlightEventItem";
import { useLazyMap } from "@/hooks/useLazyMap";
import { cn } from "@/lib/utils";

// Interface para unificar os diferentes tipos de itens
interface UnifiedItem {
  id: string;
  type: 'block' | 'tour' | 'transportation' | 'restaurant' | 'accommodation' | 'flight-departure' | 'flight-arrival' | 'accommodation-checkin' | 'accommodation-checkout';
  globalOrder: number;
  item: QuotationBlock | TourInfo | TransportationInfo | RestaurantInfo | AccommodationInfo | FlightInfo | FlightEvent | AccommodationEvent;
  flightEvent?: FlightEvent;
  flightId?: string;
  accommodationId?: string;
  isCheckIn?: boolean;
  date?: string;
  time?: string;
  notes?: string;
  accommodation?: AccommodationInfo;
}

// Type guards para ajudar o TypeScript a entender os tipos corretamente nas condicionais
function isTransportation(item: UnifiedItem): item is UnifiedItem & { type: 'transportation' } {
  return item.type === 'transportation';
}

function isRestaurant(item: UnifiedItem): item is UnifiedItem & { type: 'restaurant' } {
  return item.type === 'restaurant';
}

function isAccommodation(item: UnifiedItem): item is UnifiedItem & { type: 'accommodation' } {
  return item.type === 'accommodation';
}

function isAccommodationCheckin(item: UnifiedItem): item is UnifiedItem & { type: 'accommodation-checkin' } {
  return item.type === 'accommodation-checkin';
}

function isAccommodationCheckout(item: UnifiedItem): item is UnifiedItem & { type: 'accommodation-checkout' } {
  return item.type === 'accommodation-checkout';
}

function isFlightDeparture(item: UnifiedItem): item is UnifiedItem & { type: 'flight-departure' } {
  return item.type === 'flight-departure';
}

function isFlightArrival(item: UnifiedItem): item is UnifiedItem & { type: 'flight-arrival' } {
  return item.type === 'flight-arrival';
}

function isBlock(item: UnifiedItem): item is UnifiedItem & { type: 'block' } {
  return item.type === 'block';
}

function isTour(item: UnifiedItem): item is UnifiedItem & { type: 'tour' } {
  return item.type === 'tour';
}

interface QuotationStickyScrollProps {
  blocks: QuotationBlock[];
  tours?: TourInfo[];
  transportations?: TransportationInfo[];
  restaurants?: RestaurantInfo[];
  accommodations?: AccommodationInfo[];
  flights?: FlightInfo[]; // Adicionar suporte para flights
  flightEvents?: FlightEvent[]; // Adicionar suporte para flightEvents
  accommodationEvents?: AccommodationEvent[]; // Adicionar suporte para accommodationEvents
  scrollId?: string;
  onScrollStart?: () => void;
  showMapLines?: boolean;
  showAccommodations?: boolean;
  showMap?: boolean; // Adicionar propriedade para controlar a visibilidade da seção de mapa
  onViewAccommodationDetails?: (accommodationId: string) => void;
  onViewFlightDetails?: (flightId: string) => void; // Adicionar callback para detalhes de voo
}

interface MapCoordinate {
  lat: number;
  lng: number;
  title: string;
  id: string;
  index: number;
  isBlock?: boolean;
  isTour?: boolean;
  isRestaurant?: boolean;
  isTransportation?: boolean;
  isTransportationDeparture?: boolean;
  isTransportationArrival?: boolean;
  isAccommodation?: boolean;
  isFlightEvent?: boolean;
  isFlightDeparture?: boolean;
  isFlightArrival?: boolean;
  useTemporaryCoordinates?: boolean;
  transportationId?: string;
  accommodationId?: string;
}

// Definir a função formatDate explicitamente para uso nos cards de voo
const formatDate = (dateTimeStr?: string): string => {
  if (!dateTimeStr) return 'N/A';
  
  try {
    // Usar parseISO para garantir compatibilidade de formato
    const date = parseISO(dateTimeStr);
    
    // Forçar o tratamento como data local, sem ajuste de timezone
    const localDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    
    return format(localDate, "dd/MM/yyyy", { locale: ptBR });
  } catch (e) {
    // Em caso de erro, retornar a string original ou um valor padrão
    return dateTimeStr || 'N/A';
  }
};

// Componente para o mapa que mostra os locais dos blocos e tours
function QuotationMap({ blocks, tours = [], transportations = [], restaurants = [], accommodations = [], flights = [], flightEvents = [], accommodationEvents = [], isFullscreen = false, showMapLines = true, hoveredItemIndex = -2, unifiedItems = [] }: { 
  blocks: QuotationBlock[], 
  tours?: TourInfo[], 
  transportations?: TransportationInfo[],
  restaurants?: RestaurantInfo[],
  accommodations?: AccommodationInfo[],
  flights?: FlightInfo[],
  flightEvents?: FlightEvent[],
  accommodationEvents?: AccommodationEvent[],
  isFullscreen?: boolean,
  showMapLines?: boolean,
  hoveredItemIndex?: number,
  unifiedItems?: UnifiedItem[]
}) {
  // Usar o hook para lazy loading
  const { containerRef, isVisible, isMapInitialized, setIsMapInitialized } = useLazyMap();
  const [mapsLoaded, setMapsLoaded] = useState(isGoogleMapsLoaded());
  const markersRef = useRef<google.maps.Marker[]>([]);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const overlaysRef = useRef<any[]>([]);
  const [activeOverlay, setActiveOverlay] = useState<any | null>(null);
  // Adicionando uma referência para a versão dos dados para detectar mudanças
  const dataVersionRef = useRef<string>("");
  // ID único para cada mapa
  const mapId = useRef<string>(`map-${Math.random().toString(36).substring(2, 9)}`);

  // Helper function to create curved paths between two points
  const createCurvedPath = (p1: {lat: number, lng: number}, p2: {lat: number, lng: number}, curvature: number) => {
    // Calculate midpoint
    const midPoint = { 
      lat: (p1.lat + p2.lat) / 2,
      lng: (p1.lng + p2.lng) / 2 
    };
    
    // Calculate the perpendicular offset for the curve
    const dx = p2.lng - p1.lng;
    const dy = p2.lat - p1.lat;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    // Normalize perpendicular vector
    const nx = -dy / dist;
    const ny = dx / dist;
    
    // Apply curvature
    const curvePoint = {
      lat: midPoint.lat + curvature * dist * nx,
      lng: midPoint.lng + curvature * dist * ny
    };
    
    // Generate points along the quadratic curve (Bezier curve)
    const curvedPoints = [];
    const steps = 10; // Number of points to generate
    
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      
      // Quadratic Bezier curve formula: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
      const lat = Math.pow(1-t, 2) * p1.lat + 2 * (1-t) * t * curvePoint.lat + Math.pow(t, 2) * p2.lat;
      const lng = Math.pow(1-t, 2) * p1.lng + 2 * (1-t) * t * curvePoint.lng + Math.pow(t, 2) * p2.lng;
      
      curvedPoints.push({ lat, lng });
    }
    
    return curvedPoints;
  };

  // Inside the QuotationMap component, before the useEffect with markers
  // Add a ref to store the valid coordinates for use in the hover effect
  const validCoordinatesRef = useRef<any[]>([]);

  // Verificar mudanças nos blocos e tours
  useEffect(() => {
    // Se não estiver visível, não fazer nada
    if (!isVisible) return;
    
    // Criar uma "impressão digital" dos dados atuais para verificar mudanças
    const blocksFingerprint = blocks.map(b => b.id + (b.coordinates ? `-${b.coordinates.lat}-${b.coordinates.lng}` : "-null")).join("|");
    const toursFingerprint = tours.map(t => t.id + (t.coordinates ? `-${t.coordinates.lat}-${t.coordinates.lng}` : "-null")).join("|");
    const restaurantsFingerprint = restaurants.map(r => r.id + (r.coordinates ? `-${r.coordinates.lat}-${r.coordinates.lng}` : "-null")).join("|"); // Adicionar fingerprint para restaurants
    const newVersion = blocksFingerprint + "||" + toursFingerprint + "||" + restaurantsFingerprint; // Adicionar restaurants à impressão digital
    
    if (dataVersionRef.current !== newVersion) {
      dataVersionRef.current = newVersion;
      
      // Limpar recursos existentes
      if (mapInstanceRef.current) {
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        overlaysRef.current.forEach(overlay => overlay.setMap(null));
        overlaysRef.current = [];
      }
    }
  }, [blocks, tours, transportations, restaurants, isVisible]); // Adicionar restaurants e isVisible como dependência

  // Carregar a API do Google Maps
  useEffect(() => {
    // Só carregue quando o componente estiver visível
    if (!mapsLoaded && isVisible) {
      loadGoogleMapsApi()
        .then(() => {
          setMapsLoaded(true);
        })
        .catch(error => {
        });
    }
  }, [mapsLoaded, isVisible]);

  // Inicializar o mapa quando a API estiver carregada
  useEffect(() => {
    // Só inicialize quando estiver visível e a API estiver carregada
    if (!mapsLoaded || !containerRef.current || !isVisible) return;

    // Se o mapa já estiver inicializado, não o inicialize novamente
    if (mapInstanceRef.current && isMapInitialized) {
      // Apenas atualizar os marcadores se necessário
      return;
    }

    // Limpar quaisquer mapas antigos antes de criar um novo
    if (mapInstanceRef.current) {
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      overlaysRef.current.forEach(overlay => overlay.setMap(null));
      overlaysRef.current = [];
      // A referência do mapa será substituída abaixo
    }

    try {
      // Definição da classe CustomMarkerOverlay dentro do useEffect,
      // garantindo que o objeto google já esteja disponível
      class CustomMarkerOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private content: string;
        private div: HTMLDivElement | null = null;
        public id: string = ''; // Adicionar propriedade ID
        
        constructor(position: google.maps.LatLng, content: string) {
          super();
          this.position = position;
          this.content = content;
        }

        onAdd() {
          this.div = document.createElement('div');
          this.div.style.position = 'absolute';
          this.div.style.backgroundColor = '#0D9488'; // Changed from orange (#F97316) to teal
          this.div.style.color = 'white';
          this.div.style.padding = '6px 10px';
          this.div.style.borderRadius = '4px';
          this.div.style.fontWeight = '500';
          this.div.style.fontSize = '14px';
          this.div.style.fontFamily = "'Inter', sans-serif";
          this.div.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
          this.div.style.whiteSpace = 'nowrap';
          this.div.style.textAlign = 'center';
          this.div.style.transform = 'translate(-50%, -130%)'; // Centralizar horizontalmente e posicionar acima do marcador
          // Permitir que o mapa receba eventos de clique através do overlay
          this.div.style.pointerEvents = 'none';
          this.div.innerHTML = this.content;
          
          const panes = this.getPanes();
          panes?.overlayMouseTarget.appendChild(this.div);
        }

        draw() {
          if (!this.div) return;
          
          const overlayProjection = this.getProjection();
          const point = overlayProjection.fromLatLngToDivPixel(this.position);
          
          if (point) {
            this.div.style.left = point.x + 'px';
            this.div.style.top = point.y + 'px';
          }
        }

        onRemove() {
          if (this.div) {
            this.div.parentNode?.removeChild(this.div);
            this.div = null;
          }
        }

        hide() {
          if (this.div) {
            this.div.style.visibility = 'hidden';
          }
        }

        show() {
          if (this.div) {
            this.div.style.visibility = 'visible';
          }
        }
      }

      // Add this diagnostic section (removendo logs)
      // ... existing code ...

      // Modificar a coleta de coordenadas para usar a lista unificada se disponível
      const validCoordinates = unifiedItems && unifiedItems.length > 0 ? 
        // Usar a lista unificada ordenada por globalOrder
        unifiedItems.flatMap((unifiedItem, index) => {
          const item = unifiedItem.item;
          let result: MapCoordinate[] = [];
          
          // Verificar o tipo de item e extrair coordenadas adequadamente
          if (unifiedItem.type === 'block' && 'coordinates' in item && item.coordinates?.lat && item.coordinates?.lng) {
            result.push({
              lat: parseFloat(String(item.coordinates.lat)),
              lng: parseFloat(String(item.coordinates.lng)),
              title: 'title' in item ? item.title : `Item ${index + 1}`,
              id: unifiedItem.id,
              index: index,
              isBlock: true
            });
          } 
          else if (unifiedItem.type === 'tour' && 'coordinates' in item && item.coordinates?.lat && item.coordinates?.lng) {
            result.push({
              lat: parseFloat(String(item.coordinates.lat)),
              lng: parseFloat(String(item.coordinates.lng)),
              title: 'name' in item ? item.name : `Item ${index + 1}`,
              id: unifiedItem.id,
              index: index, // Guardar o índice do item na lista unificada
              isTour: true
            });
          }
          else if (unifiedItem.type === 'restaurant' && 'coordinates' in item && item.coordinates?.lat && item.coordinates?.lng) {
            result.push({
              lat: parseFloat(String(item.coordinates.lat)),
              lng: parseFloat(String(item.coordinates.lng)),
              title: 'name' in item ? item.name : `Item ${index + 1}`,
              id: unifiedItem.id,
              index: index, // Guardar o índice do item na lista unificada
              isRestaurant: true
            });
          }
          else if (unifiedItem.type === 'transportation') {
            // Para transportations, precisamos lidar com os diferentes tipos de coordenadas
            const transportation = item as TransportationInfo;
            
            if (transportation.departureCoordinates?.lat && transportation.departureCoordinates?.lng) {
              const lat = parseFloat(String(transportation.departureCoordinates.lat));
              const lng = parseFloat(String(transportation.departureCoordinates.lng));
              
              if (!isNaN(lat) && !isNaN(lng)) {
                result.push({
                  lat,
                  lng,
                  title: `${transportation.name} (Partida: ${transportation.departureLocation || ''})`,
                  id: `${unifiedItem.id}-departure`,
                  index: index,
                  isTransportation: true,
                  isTransportationDeparture: true,
                  transportationId: unifiedItem.id
                });
              }
            }
            
            if (transportation.arrivalCoordinates?.lat && transportation.arrivalCoordinates?.lng) {
              const lat = parseFloat(String(transportation.arrivalCoordinates.lat));
              const lng = parseFloat(String(transportation.arrivalCoordinates.lng));
              
              if (!isNaN(lat) && !isNaN(lng)) {
                result.push({
                  lat,
                  lng,
                  title: `${transportation.name} (Chegada: ${transportation.arrivalLocation || ''})`,
                  id: `${unifiedItem.id}-arrival`,
                  index: index,
                  isTransportation: true,
                  isTransportationArrival: true,
                  transportationId: unifiedItem.id
                });
              }
            }
            
            if (result.length === 0 && transportation.coordinates?.lat && transportation.coordinates?.lng) {
              const lat = parseFloat(String(transportation.coordinates.lat));
              const lng = parseFloat(String(transportation.coordinates.lng));
              
              if (!isNaN(lat) && !isNaN(lng)) {
                result.push({
                  lat,
                  lng,
                  title: transportation.name,
                  id: transportation.id,
                  index: 0, // Use 0 as a default index
                  isTransportation: true,
                  transportationId: transportation.id
                });
              }
            }
          }
          else if (unifiedItem.type === 'accommodation' || unifiedItem.type === 'accommodation-checkin' || unifiedItem.type === 'accommodation-checkout') {
            const accommodation = item as AccommodationInfo;
            if (accommodation.coordinates?.lat && accommodation.coordinates?.lng) {
              const lat = parseFloat(String(accommodation.coordinates.lat));
              const lng = parseFloat(String(accommodation.coordinates.lng));
              
              if (!isNaN(lat) && !isNaN(lng)) {
                result.push({
                  lat,
                  lng,
                  title: accommodation.name || `Hospedagem ${index + 1}`,
                  id: unifiedItem.id,
                  index: index,
                  isAccommodation: true,
                  accommodationId: accommodation.id
                });
              }
            }
          }
          // Adicionar lógica para eventos de voo (partida e chegada)
          else if (unifiedItem.type === 'flight-departure' || unifiedItem.type === 'flight-arrival') {
            // Extrair o flightEvent e o flightId diretamente do unifiedItem
            const flightEvent = unifiedItem.item as FlightEvent;
            const isDeparture = unifiedItem.type === 'flight-departure';
            
            // Usar o flightId do unifiedItem se disponível, caso contrário do flightEvent
            const flightId = unifiedItem.flightId || (flightEvent && flightEvent.flightId);
            const flight = flightId ? flights?.find(f => f.id === flightId) : null;
            
            // Check if the FlightEvent has _coordinates property (added during enrichment)
            if (flightEvent && flightEvent._coordinates && flightEvent._coordinates.lat && flightEvent._coordinates.lng) {
              const lat = parseFloat(String(flightEvent._coordinates.lat));
              const lng = parseFloat(String(flightEvent._coordinates.lng));
              
              if (!isNaN(lat) && !isNaN(lng)) {
                // Use the enriched title if available
                const title = flightEvent.title || `${isDeparture ? 'Partida' : 'Chegada'} de Voo`;
                
                result.push({
                  lat,
                  lng,
                  title,
                  id: unifiedItem.id,
                  index: index,
                  isFlightEvent: true,
                  isFlightDeparture: isDeparture,
                  isFlightArrival: !isDeparture,
                  useTemporaryCoordinates: false
                });
                
                // Log for debugging
                console.log(`[QuotationMap] FlightEvent ID=${flightEvent.id} displayed with coordinates:`, {
                  lat, lng, title
                });
              }
            // Então tentar usar as coordenadas do voo associado diretamente
            } else if (flight) {
              // Use the appropriate coordinates based on departure/arrival
              let coordinates = isDeparture ? 
                flight.departureCoordinates : 
                flight.arrivalCoordinates;
              
              // If no coordinates, create temporary ones
              if (!coordinates || !coordinates.lat || !coordinates.lng) {
                const tempCoordinates = {
                  lat: -23.5505 + (index * 0.01), // São Paulo como ponto de partida, com incremento por índice
                  lng: -46.6333 + (index * 0.01)
                };
                
                coordinates = tempCoordinates;
              }
              
              const lat = parseFloat(String(coordinates.lat));
              const lng = parseFloat(String(coordinates.lng));
              
              if (!isNaN(lat) && !isNaN(lng)) {
                // Use flightEvent title if available, otherwise construct one
                const title = (flightEvent && flightEvent.title) || 
                  `${flight.airline || ''} ${flight.flightNumber || ''} (${isDeparture ? 'Partida' : 'Chegada'})`;
                
                result.push({
                  lat,
                  lng,
                  title,
                  id: unifiedItem.id,
                  index: index,
                  isFlightEvent: true,
                  isFlightDeparture: isDeparture,
                  isFlightArrival: !isDeparture,
                  useTemporaryCoordinates: !flight.departureCoordinates && !flight.arrivalCoordinates
                });
                
                // Log for debugging
                console.log(`[QuotationMap] FlightEvent displayed using Flight coordinates:`, {
                  lat, lng, title, flightId: flight.id, unifiedItemId: unifiedItem.id
                });
              }
            } else {
              // If we can't find the flight, use temporary coordinates
              const tempCoordinates = {
                lat: -23.5505 + (index * 0.01),
                lng: -46.6333 + (index * 0.01)
              };
              
              const lat = parseFloat(String(tempCoordinates.lat));
              const lng = parseFloat(String(tempCoordinates.lng));
              
              // Use flightEvent title if available, otherwise use a default
              const title = (flightEvent && flightEvent.title) || `${isDeparture ? 'Partida' : 'Chegada'} de Voo`;
              
              result.push({
                lat,
                lng,
                title,
                id: unifiedItem.id,
                index: index,
                isFlightEvent: true,
                isFlightDeparture: isDeparture,
                isFlightArrival: !isDeparture,
                useTemporaryCoordinates: true
              });
              
              // Log warning for debugging
              console.warn(`[QuotationMap] FlightEvent using temporary coordinates - Flight not found:`, {
                flightId, title, unifiedItemId: unifiedItem.id
              });
            }
          }
          
          // Filtrar valores inválidos
          return result.filter(coord => !isNaN(coord.lat) && !isNaN(coord.lng));
        })
        : 
        // Se não houver lista unificada, usar o comportamento original
        [
          // Blocos com coordenadas
          ...blocks
            .filter(block => block.coordinates?.lat && block.coordinates?.lng)
            .map(block => ({
              lat: parseFloat(block.coordinates!.lat.toString()),
              lng: parseFloat(block.coordinates!.lng.toString()),
              title: block.title || `Bloco ${blocks.indexOf(block) + 1}`,
              id: block.id,
              isBlock: true
            })),
          // Tours com coordenadas
          ...tours
            .filter(tour => tour.coordinates && (tour.coordinates.lat !== undefined) && (tour.coordinates.lng !== undefined))
            .map(tour => {
              const lat = parseFloat(String(tour.coordinates!.lat));
              const lng = parseFloat(String(tour.coordinates!.lng));
              
              return {
                lat,
                lng,
                title: tour.name || 'Tour',
                id: tour.id,
                isTour: true
              };
            })
            .filter(coord => !isNaN(coord.lat) && !isNaN(coord.lng)),
          // Restaurantes com coordenadas
          ...restaurants
            .filter(restaurant => restaurant.coordinates && (restaurant.coordinates.lat !== undefined) && (restaurant.coordinates.lng !== undefined))
            .map(restaurant => {
              const lat = parseFloat(String(restaurant.coordinates!.lat));
              const lng = parseFloat(String(restaurant.coordinates!.lng));
              
              return {
                lat,
                lng,
                title: restaurant.name || 'Restaurante',
                id: restaurant.id,
                isRestaurant: true
              };
            })
            .filter(coord => !isNaN(coord.lat) && !isNaN(coord.lng)),
          // Transportation coordinates - both departure and arrival
          ...(transportations || [])
            .filter(transportation => (
              (transportation.departureCoordinates?.lat && transportation.departureCoordinates?.lng) || 
              (transportation.arrivalCoordinates?.lat && transportation.arrivalCoordinates?.lng) ||
              (transportation.coordinates?.lat && transportation.coordinates?.lng)
            ))
            .flatMap(transportation => {
              const result = [];
              
              // Add departure coordinates if they exist
              if (transportation.departureCoordinates?.lat && transportation.departureCoordinates?.lng) {
                const lat = parseFloat(String(transportation.departureCoordinates.lat));
                const lng = parseFloat(String(transportation.departureCoordinates.lng));
                
                if (!isNaN(lat) && !isNaN(lng)) {
                  result.push({
                    lat,
                    lng,
                    title: `${transportation.name} (Partida: ${transportation.departureLocation || ''})`,
                    id: `${transportation.id}-departure`,
                    isTransportation: true,
                    isTransportationDeparture: true,
                    transportationId: transportation.id // Add this to track pairs
                  });
                }
              }
              
              // Add arrival coordinates if they exist
              if (transportation.arrivalCoordinates?.lat && transportation.arrivalCoordinates?.lng) {
                const lat = parseFloat(String(transportation.arrivalCoordinates.lat));
                const lng = parseFloat(String(transportation.arrivalCoordinates.lng));
                
                if (!isNaN(lat) && !isNaN(lng)) {
                  result.push({
                    lat,
                    lng,
                    title: `${transportation.name} (Chegada: ${transportation.arrivalLocation || ''})`,
                    id: `${transportation.id}-arrival`,
                    isTransportation: true,
                    isTransportationArrival: true,
                    transportationId: transportation.id // Add this to track pairs
                  });
                }
              }
              
              // Add generic coordinates if they exist and no specific departure/arrival
              if (result.length === 0 && transportation.coordinates?.lat && transportation.coordinates?.lng) {
                const lat = parseFloat(String(transportation.coordinates.lat));
                const lng = parseFloat(String(transportation.coordinates.lng));
                
                if (!isNaN(lat) && !isNaN(lng)) {
                  result.push({
                    lat,
                    lng,
                    title: transportation.name,
                    id: transportation.id,
                    index: 0, // Use 0 as a default index
                    isTransportation: true,
                    transportationId: transportation.id
                  });
                }
              }
              
              return result;
            })
        ];

      // Se não houver coordenadas válidas, mostrar um mapa padrão
      if (validCoordinates.length === 0) {
        // Coordenadas padrão (Lisboa, Portugal)
        const defaultCoord = {
          lat: 38.7223,
          lng: -9.1393,
          title: "Destino",
          id: "default",
          index: 0,
          isBlock: true,
          isTour: false,
          isRestaurant: false,
          isTransportation: false,
          isTransportationDeparture: false,
          isTransportationArrival: false,
          isAccommodation: false
        } as MapCoordinate;
        
        validCoordinates.push(defaultCoord);
      }

      // Calcular o centro do mapa (média das coordenadas)
      const center = validCoordinates.reduce(
        (acc, coord) => {
          acc.lat += coord.lat / validCoordinates.length;
          acc.lng += coord.lng / validCoordinates.length;
          return acc;
        },
        { lat: 0, lng: 0 }
      );

      // Criar o mapa
      const map = new window.google.maps.Map(containerRef.current, {
        center,
        zoom: validCoordinates.length === 1 ? 13 : 10,
        mapTypeControl: isFullscreen, // Mostrar controles de tipo de mapa apenas no modo tela cheia
        fullscreenControl: false,
        streetViewControl: isFullscreen, // Mostrar Street View apenas no modo tela cheia
        zoomControl: true,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ]
      });

      mapInstanceRef.current = map;
      
      // Variável para rastrear o ID do overlay ativo
      let currentOverlayId: string | null = null;

      // Função para fechar todos os overlays
      const closeAllOverlays = () => {
        overlaysRef.current.forEach(overlay => {
          overlay.setMap(null);
        });
        setActiveOverlay(null);
        currentOverlayId = null;
      };

      // Limpar marcadores anteriores
      markersRef.current.forEach(marker => {
        marker.setMap(null);
      });
      markersRef.current = [];
      
      // Limpar overlays anteriores
      overlaysRef.current.forEach(overlay => {
        overlay.setMap(null);
      });
      overlaysRef.current = [];

      // Adicionar marcadores para cada coordenada
      validCoordinates.forEach((coord, index) => {
        try {
          // Determinar o número do marcador - usar o índice do item unificado se disponível
          const markerNumber = 'index' in coord ? (coord.index as number) + 1 : index + 1;
          
          // Determine if this marker should have a label (number)
          // Pontos de chegada de transporte não devem mostrar número para não interferir na sequência
          const shouldHaveLabel = !('isTransportation' in coord && 'isTransportationArrival' in coord);
          
          // Create marker with default styling (non-highlighted)
          const marker = new window.google.maps.Marker({
            position: { lat: coord.lat, lng: coord.lng },
            map,
            title: coord.title,
            label: shouldHaveLabel ? {
              text: `${markerNumber}`,
              color: "#FFFFFF",
              fontWeight: "bold"
            } : null,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              // Cores diferentes para pontos de partida e chegada de transportes
              fillColor: ('isTransportation' in coord) 
                ? ('isTransportationDeparture' in coord) 
                  ? '#0D9488' // Teal para partida
                  : '#94a3b8' // Cinza para chegada
                : ('isFlightEvent' in coord)
                  ? '#0D9488' // Teal para eventos de voo (mesmo padrão dos outros itens)
                  : '#0D9488', // Teal padrão para outros pontos
              fillOpacity: 1,
              strokeWeight: ('isTransportation' in coord || 'isFlightEvent' in coord) ? 2 : 0, // Only add stroke for transportation and flight events
              // Bordas com cores diferentes para destacar visualmente
              strokeColor: ('isTransportation' in coord) 
                ? ('isTransportationDeparture' in coord)
                  ? '#EAB308' // Amarelo para partida  
                  : '#475569' // Cinza escuro para chegada
                : ('isFlightEvent' in coord)
                  ? '#EAB308' // Amarelo para eventos de voo
                  : '',
              // Tamanhos diferentes para pontos de partida e chegada
              scale: (('isTransportation' in coord && 'isTransportationArrival' in coord) || 
                      ('isFlightEvent' in coord && 'isFlightArrival' in coord)) ? 8 : 12
            },
            zIndex: (('isTransportation' in coord && 'isTransportationArrival' in coord) || 
                     ('isFlightEvent' in coord && 'isFlightArrival' in coord)) ? index - 1 : index
          });

          markersRef.current.push(marker);

          // Criar um Custom Overlay para o marcador
          const overlay = new CustomMarkerOverlay(
            new google.maps.LatLng(coord.lat, coord.lng),
            coord.title
          );
          
          // Adicionar um ID único ao overlay
          overlay.id = `overlay-${index}`;
          
          // Adicionar o overlay ao mapa
          overlay.setMap(null); // Inicialmente oculto
          overlaysRef.current.push(overlay);

          // Adicionar evento de clique ao marcador
          marker.addListener("click", (e: any) => {
            // Impedir propagação do evento se possível
            if (e && e.stop) e.stop();
            
            // Se o overlay atual já está ativo, feche-o
            if (currentOverlayId === overlay.id) {
              overlay.setMap(null);
              setActiveOverlay(null);
              currentOverlayId = null;
              return;
            }
            
            // Fechar todos os overlays ativos antes de abrir o novo
            closeAllOverlays();
            
            // Mostrar o novo overlay
            overlay.setMap(map);
            setActiveOverlay(overlay);
            currentOverlayId = overlay.id;
          });
        } catch (error) {
          console.error(`[DEBUG-MAP ${mapId.current}] Erro ao criar marcador:`, error, coord);
        }
      });

      // After creating all markers, add dashed lines between transportation departure and arrival points
      // Group coordinates by transportation ID
      const transportationCoordinatesByID: Record<string, MapCoordinate[]> = validCoordinates
        .filter(coord => 'transportationId' in coord)
        .reduce((acc, coord) => {
          const transportationId = (coord as MapCoordinate).transportationId as string;
          if (!acc[transportationId]) {
            acc[transportationId] = [];
          }
          acc[transportationId].push(coord as MapCoordinate);
          return acc;
        }, {} as Record<string, MapCoordinate[]>);

      // Draw dashed lines between departure and arrival points
      Object.values(transportationCoordinatesByID).forEach(coordsForTransportation => {
        // Only proceed if we have at least two points (departure and arrival)
        if (coordsForTransportation.length >= 2) {
          try {
            // Sort to ensure departure is first, arrival is second
            const sortedCoords = [...coordsForTransportation].sort((a, b) => {
              if ('isTransportationDeparture' in a) return -1;
              if ('isTransportationDeparture' in b) return 1;
              return 0;
            });
            
            // Create a dashed line between the points
            const dashedLine = new google.maps.Polyline({
              path: [
                { lat: sortedCoords[0].lat, lng: sortedCoords[0].lng },
                { lat: sortedCoords[1].lat, lng: sortedCoords[1].lng }
              ],
              geodesic: true,
              strokeColor: '#0D9488', // Match regular teal color
              strokeOpacity: 0.8,
              strokeWeight: 2,
              strokePattern: [8, 4] // 8px dash, 4px gap
            } as any); // Cast to any to avoid TypeScript error with strokePattern
            
            dashedLine.setMap(map);
          } catch (error) {
            console.error('Error creating dashed line for transportation:', error);
          }
        }
      });

      // Criar linhas curvas entre os pontos não-transportation se houver mais de um ponto
      if (validCoordinates.length > 1 && showMapLines) {
        try {
          // Filtrar somente pontos que não são transportation para as linhas curvas regulares
          const nonTransportationCoords = validCoordinates.filter(coord => !('isTransportation' in coord));
          
          if (nonTransportationCoords.length > 1) {
            // Criar linhas curvas entre todos os pontos
            const allCurvedPoints = [];

            // Para cada par de pontos consecutivos
            for (let i = 0; i < nonTransportationCoords.length - 1; i++) {
              const p1 = { lat: nonTransportationCoords[i].lat, lng: nonTransportationCoords[i].lng };
              const p2 = { lat: nonTransportationCoords[i+1].lat, lng: nonTransportationCoords[i+1].lng };
              
              // Obter os pontos da curva entre p1 e p2
              const curvedPoints = createCurvedPath(p1, p2, 0.15); // Curvatura leve
              
              // Adicionar ao array total (exceto o último ponto para evitar duplicação)
              if (i < nonTransportationCoords.length - 2) {
                allCurvedPoints.push(...curvedPoints.slice(0, -1));
              } else {
                allCurvedPoints.push(...curvedPoints);
              }
            }
            
            // Criar a polyline com todos os pontos curvados
            const curvedLine = new google.maps.Polyline({
              path: allCurvedPoints,
              geodesic: false, // Importante: false para manter a curva como calculada
              strokeColor: '#0D9488', // Teal color for non-transportation lines
              strokeOpacity: 0.7,
              strokeWeight: 2
            });
            
            curvedLine.setMap(map);
          }
        } catch (error) {
          console.error('Erro ao criar linhas curvas:', error);
        }
      }

      // Adicionar evento de clique no mapa para fechar o overlay ativo
      map.addListener("click", () => {
        closeAllOverlays();
      });

      // Ajustar o zoom para mostrar todos os marcadores
      if (validCoordinates.length > 1) {
        try {
          const bounds = new window.google.maps.LatLngBounds();
          validCoordinates.forEach(coord => {
            bounds.extend({ lat: coord.lat, lng: coord.lng });
          });
          map.fitBounds(bounds);
        } catch (error) {
          console.error('Erro ao ajustar zoom:', error);
        }
      }

      // Store validCoordinates for use in hover effect
      validCoordinatesRef.current = validCoordinates;
      
      // Marcar o mapa como inicializado no final
      setIsMapInitialized(true);
    } catch (error) {
      console.error("Erro ao inicializar o mapa:", error);
    }
  }, [mapsLoaded, isVisible, containerRef, isMapInitialized, setIsMapInitialized, unifiedItems, showMapLines, hoveredItemIndex]); // Adicionar dependências

  // Atualizar os marcadores quando hoveredItemIndex mudar
  useEffect(() => {
    if (!mapInstanceRef.current || !mapsLoaded || markersRef.current.length === 0) return;
    
    // Criar um mapeamento entre índices originais dos marcadores e índices de hover
    // Este array relaciona cada marcador ao índice de hover que deve ser usado para ele
    const markerToHoverIndexMap: number[] = [];
    
    // Contador para rastrear apenas os pontos que devem ser considerados na sequência de hover
    let hoverCounter = 0;
    
    // Preencher o mapeamento de índices
    validCoordinatesRef.current.forEach((coord, index) => {
      // Se for um ponto de chegada de transporte, mapear para -3 (nunca será destacado pelo hover)
      if (coord && 'isTransportationArrival' in coord && coord.isTransportationArrival) {
        markerToHoverIndexMap[index] = -3; // Valor especial que nunca corresponderá ao hover
      } else if (coord && 'index' in coord) {
        // Se a coordenada tiver um índice definido, usar esse índice diretamente
        // Isso é crucial para eventos de voo e outros itens unificados
        markerToHoverIndexMap[index] = coord.index as number;
      } else {
        // Caso contrário, atribuir o próximo índice sequencial de hover
        markerToHoverIndexMap[index] = hoverCounter++;
      }
    });
    
    // Atualizar cada marcador baseado no mapeamento correto de hoveredItemIndex
    markersRef.current.forEach((marker, index) => {
      // Determinar se este marcador deve ser destacado com base no mapeamento
      const mappedHoverIndex = markerToHoverIndexMap[index];
      const isHighlighted = mappedHoverIndex === hoveredItemIndex;
      
      const coords = validCoordinatesRef.current[index]; // Get the original coordinate data
      
      // Determinar a cor correta baseada no tipo do ponto
      // Mantém a consistência com as cores definidas na criação dos marcadores
      let normalColor;
      
      if (coords) {
        if ('isTransportation' in coords) {
          if ('isTransportationDeparture' in coords) {
            normalColor = "#0D9488"; // Teal para pontos de partida de transporte
          } else if ('isTransportationArrival' in coords) {
            normalColor = "#94a3b8"; // Cinza para pontos de chegada de transporte
          } else {
            normalColor = "#07a1a5"; // Teal personalizado para transportes gerais
          }
        } else if ('isFlightEvent' in coords) {
          normalColor = coords.isFlightDeparture ? "#0D9488" : "#94a3b8"; // Teal para partida, cinza para chegada
        } else if ('isTour' in coords && coords.isTour) {
          normalColor = "#EAB308"; // Amarelo para tours
        } else if ('isRestaurant' in coords && coords.isRestaurant) {
          normalColor = "#ef4444"; // Vermelho para restaurantes
        } else {
          normalColor = "#0D9488"; // Teal padrão para blocos
        }
      } else {
        normalColor = "#0D9488"; // Cor padrão para segurança
      }
      
      // Determinar a borda com base no tipo do ponto
      let strokeColor = "";
      if (coords && 'isTransportation' in coords) {
        strokeColor = 'isTransportationDeparture' in coords 
          ? '#EAB308' // Amarelo para partida  
          : '#475569'; // Cinza escuro para chegada
      } else if (coords && 'isFlightEvent' in coords) {
        strokeColor = coords.isFlightDeparture 
          ? '#EAB308' // Amarelo para partida de voo
          : '#475569'; // Cinza escuro para chegada de voo
      }
      
      // Determinar o tamanho com base no tipo e estado
      let scale = 12; // Tamanho padrão
      if (isHighlighted) {
        scale = 16; // Maior quando destacado
      } else if (coords && (
          ('isTransportation' in coords && 'isTransportationArrival' in coords) ||
          ('isFlightEvent' in coords && 'isFlightArrival' in coords)
      )) {
        scale = 8; // Menor para pontos de chegada (transporte ou voo)
      }
      
      // Atualizar o ícone do marcador com os tipos corretos
      marker.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        fillColor: isHighlighted ? "#F97316" : normalColor, // Laranja quando destacado, cor baseada no tipo caso contrário
        fillOpacity: 1,
        strokeWeight: (coords && ('isTransportation' in coords || 'isFlightEvent' in coords)) ? 2 : 0,
        strokeColor: strokeColor,
        scale: scale
      });

      // Ajustar o z-index para trazer o marcador destacado para a frente
      if (isHighlighted) {
        marker.setZIndex(100);
      } else if (coords && 'isTransportation' in coords && 'isTransportationArrival' in coords) {
        marker.setZIndex(index - 1); // Pontos de chegada ficam abaixo
      } else {
        marker.setZIndex(index);
      }
    });
    
    // Solução implementada:
    // 1. Criamos um mapeamento entre o índice real dos marcadores e o índice de hover
    // 2. Excluímos os pontos de chegada de transporte da sequência de hover, atribuindo um valor especial
    // 3. Para os demais pontos, atribuímos índices sequenciais começando de 0
    // 4. Diferenciamos visualmente os pontos de partida e chegada com cores e tamanhos diferentes
    // 5. Isso mantém os pontos de chegada no mapa, mas sem interferir na ordem de navegação do hover
  }, [hoveredItemIndex, mapsLoaded, mapId]);

  return (
    <div 
      className={cn(
        "relative w-full h-full overflow-hidden border border-gray-200 rounded-md bg-gray-50",
        isFullscreen ? "rounded-none" : ""
      )}
    >
      {/* Container do mapa */}
      <div 
        ref={containerRef} 
        id={mapId.current} 
        className="w-full h-full" 
        style={{ background: "#f1f5f9" }}
      ></div>
      
      {/* Camada de loading ou placeholder enquanto o mapa não está visível ou carregado */}
      {!isVisible ? (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-sm text-gray-500">O mapa será carregado quando estiver visível</div>
        </div>
      ) : !mapsLoaded ? (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-sm text-gray-500 flex items-center">
            <div className="animate-spin mr-1 h-4 w-4 border-2 border-gray-500 border-opacity-50 border-t-teal-500 rounded-full"></div>
            Carregando mapa...
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Componente para o modal do mapa em tela cheia
function MapModal({ isOpen, onClose, blocks, tours = [], transportations = [], restaurants = [], accommodations = [], flights = [], flightEvents = [], accommodationEvents = [], unifiedItems = [] }: { 
  isOpen: boolean, 
  onClose: () => void, 
  blocks: QuotationBlock[], 
  tours?: TourInfo[],
  transportations?: TransportationInfo[],
  restaurants?: RestaurantInfo[],
  accommodations?: AccommodationInfo[],
  flights?: FlightInfo[],
  flightEvents?: FlightEvent[],
  accommodationEvents?: AccommodationEvent[],
  unifiedItems?: UnifiedItem[]
}) {
  return (
    <div 
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose} // Fechar ao clicar fora do modal
    >
      <div 
        className="bg-white rounded-lg w-full max-w-6xl h-[80vh] relative overflow-hidden"
        onClick={e => e.stopPropagation()} // Impedir que cliques no modal fechem ele
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-[#F97316] text-white rounded-full p-2 z-10 hover:bg-[#EA580C] transition-colors shadow-md"
          aria-label="Fechar mapa"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="h-full w-full">
          {isOpen && <QuotationMap blocks={blocks} tours={tours} transportations={transportations} restaurants={restaurants} accommodations={accommodations} flights={flights} flightEvents={flightEvents} accommodationEvents={accommodationEvents} unifiedItems={unifiedItems} isFullscreen={true} />}
        </div>
      </div>
    </div>
  );
}

export function QuotationStickyScroll({ 
  blocks, 
  tours = [], 
  transportations = [], 
  restaurants = [], 
  accommodations = [],
  flights = [], 
  flightEvents = [], 
  accommodationEvents = [], 
  scrollId = 'default', 
  onScrollStart, 
  showMapLines = true,
  showAccommodations = false,
  showMap = true,
  onViewAccommodationDetails,
  onViewFlightDetails
}: QuotationStickyScrollProps) {
  // Debugging log for input props
  console.log(`[QuotationStickyScroll] Rendering with props:`, {
    scrollId,
    blocksCount: blocks.length,
    toursCount: tours.length,
    accommodationsCount: accommodations.length,
    accommodationEventsCount: accommodationEvents.length,
    flightsCount: flights.length,
    flightEventsCount: flightEvents.length,
    showAccommodations
  });

  // Log detailed accommodations info
  console.log(`[QuotationStickyScroll] Accommodations details:`, 
    accommodations.map(acc => ({
      id: acc.id,
      name: acc.name,
      hasCoordinates: !!(acc.coordinates?.lat && acc.coordinates?.lng)
    }))
  );

  // Log detailed accommodationEvents info
  console.log(`[QuotationStickyScroll] AccommodationEvents details:`, 
    accommodationEvents.map(event => ({
      id: event.id,
      accommodationId: event.accommodationId,
      isCheckIn: event.isCheckIn,
      title: event.title,
      globalOrder: event.globalOrder
    }))
  );

  // Inicializar com a seção introdutória como ativa (-1)
  const [activeIndex, setActiveIndex] = useState(-1); 
  const [hoveredItemIndex, setHoveredItemIndex] = useState(-2); // -2 significa nenhum item em hover
  const containerRef = useRef<HTMLDivElement>(null);
  const textSectionsRef = useRef<(HTMLDivElement | null)[]>([]);
  const introSectionRef = useRef<HTMLDivElement>(null);
  
  // Estado para armazenar os itens unificados
  const [unifiedItems, setUnifiedItems] = useState<UnifiedItem[]>([]);
  
  // Estado para controlar qual imagem está sendo exibida para cada item
  const [currentImageIndices, setCurrentImageIndices] = useState<number[]>([]);
  
  // Estado para controlar a abertura do modal do mapa
  const [mapModalOpen, setMapModalOpen] = useState(false);
  // Estado para controlar se o mapa foi inicializado - começando com true para força-lo a renderizar
  const [mapInitialized, setMapInitialized] = useState(true);
  // ID único para esta instância do componente
  const componentId = useRef<string>(`quotation-sticky-scroll-${scrollId}-${Math.random().toString(36).substring(2, 9)}`);
  // Referência para controlar se o onScrollStart já foi chamado
  const hasCalledScrollStart = useRef(false);
  // Total de itens (usando a nova estrutura unificada)
  const totalItems = unifiedItems.length;

  // Unificar os itens e ordená-los por globalOrder
  useEffect(() => {
    const newItems: UnifiedItem[] = [];
    
    // Adicionar blocos
    blocks.forEach(block => {
      // Não adicionar blocos que são descrições de página
      if ((block as any).isPageDescription) {
        return;
      }
      
      newItems.push({
        id: block.id,
        type: 'block',
        globalOrder: block.globalOrder ?? 999999,
        item: block
      });
    });
    
    // Adicionar tours
    tours.forEach(tour => {
      newItems.push({
        id: tour.id,
        type: 'tour',
        globalOrder: tour.globalOrder ?? 999999,
        item: tour
      });
    });
    
    // Adicionar transportations
    if (transportations.length) {
      transportations.forEach(transportation => {
        newItems.push({
          id: transportation.id,
          type: 'transportation',
          globalOrder: transportation.globalOrder ?? 999999,
          item: transportation
        });
      });
    }
    
    // Adicionar restaurants
    if (restaurants.length) {
      restaurants.forEach(restaurant => {
        newItems.push({
          id: restaurant.id,
          type: 'restaurant',
          globalOrder: restaurant.globalOrder ?? 999999,
          item: restaurant
        });
      });
    }
    
    // Adicionar accommodations se showAccommodations for true
    if (accommodations && accommodations.length && showAccommodations) {
      accommodations.forEach(accommodation => {
        if (showAccommodations) {
          newItems.push({
            id: accommodation.id,
            type: 'accommodation',
            globalOrder: accommodation.globalOrder ?? 999999,
            item: accommodation
          });
        }
      });
    }
    
    // Adicionar flights como FlightEvent simulados
    if (flights && flights.length) {
      flights.forEach(flight => {
        if (flight.showAsSeparateEvents) {
          // Add departure event
          newItems.push({
            id: `${flight.id}-departure`,
            type: 'flight-departure',
            globalOrder: flight.departureGlobalOrder ?? flight.globalOrder ?? 999999,
            item: flight
          });
          
          // Add arrival event
          newItems.push({
            id: `${flight.id}-arrival`,
            type: 'flight-arrival',
            globalOrder: flight.arrivalGlobalOrder ?? flight.globalOrder ?? 999999,
            item: flight
          });
        }
      });
    }
    
    // Adicionar accommodationEvents
    if (accommodationEvents && accommodationEvents.length) {
      console.log(`[QuotationStickyScroll] Processing ${accommodationEvents.length} accommodationEvents`);
      
      accommodationEvents.forEach(accommodationEvent => {
        // Buscar a hospedagem associada
        const accommodation = accommodations.find(a => a.id === accommodationEvent.accommodationId);
        
        console.log(`[QuotationStickyScroll] Processing accommodationEvent:`, {
          eventId: accommodationEvent.id,
          accommodationId: accommodationEvent.accommodationId,
          isCheckIn: accommodationEvent.isCheckIn,
          foundAccommodation: !!accommodation,
          accommodationName: accommodation?.name
        });
        
        if (accommodation) {
          // Log para debug mostrando que a hospedagem foi encontrada
          console.log(`[QuotationStickyScroll] Accommodation found for AccommodationEvent ${accommodationEvent.id}:`, {
            accommodationId: accommodation.id,
            name: accommodation.name,
            location: accommodation.location,
            coordinates: accommodation.coordinates,
            checkInDate: accommodation.checkInDate,
            checkOutDate: accommodation.checkOutDate
          });
          
          // Enriquecer com informações da hospedagem para display no mapa
          const enrichedAccommodationEvent = {
            ...accommodationEvent,
            _displayDates: accommodationEvent.isCheckIn 
              ? (accommodation.checkInDate ? accommodation.checkInDate.split('T')[0].split('-').reverse().join('/') : undefined)
              : (accommodation.checkOutDate ? accommodation.checkOutDate.split('T')[0].split('-').reverse().join('/') : undefined),
            _displayTime: accommodationEvent.isCheckIn 
              ? accommodation.checkInTime
              : accommodation.checkOutTime,
            _displayLocation: accommodation.location,
            _coordinates: accommodation.coordinates,
            coordinates: accommodation.coordinates,
            name: accommodation.name,
            location: accommodation.location,
            images: accommodation.images
          };
          
          // Create the unified item
          const unifiedItem = {
            id: accommodationEvent.id,
            type: accommodationEvent.isCheckIn ? 'accommodation-checkin' : 'accommodation-checkout' as 'accommodation-checkin' | 'accommodation-checkout',
            globalOrder: accommodationEvent.globalOrder ?? accommodation.globalOrder ?? 999999,
            item: enrichedAccommodationEvent,
            accommodation: accommodation,
            accommodationId: accommodationEvent.accommodationId
          };
          
          newItems.push(unifiedItem);
          
          console.log(`[QuotationStickyScroll] Added enriched AccommodationEvent to unifiedItems:`, {
            id: unifiedItem.id,
            type: unifiedItem.type,
            accommodationId: unifiedItem.accommodationId,
            globalOrder: unifiedItem.globalOrder,
            hasAccommodation: !!unifiedItem.accommodation
          });
        } else {
          // Adicionar mesmo sem encontrar a hospedagem (a lógica de fallback vai lidar com isso)
          console.warn(`[QuotationStickyScroll] Warning: AccommodationEvent ID=${accommodationEvent.id} has no associated accommodation found. accommodationId=${accommodationEvent.accommodationId}`);
          
          // Mesmo sem encontrar a hospedagem, ainda adicionar ao array de itens
          const fallbackItem = {
            id: accommodationEvent.id,
            type: accommodationEvent.isCheckIn ? 'accommodation-checkin' : 'accommodation-checkout' as 'accommodation-checkin' | 'accommodation-checkout',
            globalOrder: accommodationEvent.globalOrder ?? 999999,
            item: {
              ...accommodationEvent,
              _coordinates: null,
              coordinates: null,
              name: accommodationEvent.title || (accommodationEvent.isCheckIn ? 'Check-in' : 'Check-out'),
              location: 'Local não especificado'
            },
            accommodationId: accommodationEvent.accommodationId
          };
          
          newItems.push(fallbackItem);
          
          console.warn(`[QuotationStickyScroll] Added fallback AccommodationEvent to unifiedItems:`, {
            id: fallbackItem.id,
            type: fallbackItem.type,
            accommodationId: fallbackItem.accommodationId,
            globalOrder: fallbackItem.globalOrder,
            title: accommodationEvent.title || (accommodationEvent.isCheckIn ? 'Check-in' : 'Check-out')
          });
        }
      });
    } else {
      console.log(`[QuotationStickyScroll] No accommodationEvents to process:`, {
        accommodationEventsCount: accommodationEvents?.length || 0,
        // Removido a dependência de showAccommodations
      });
    }
    
    // Adicionar flightEvents reais
    if (flightEvents && flightEvents.length) {
      flightEvents.forEach(flightEvent => {
        // Enriquecer flightEvent com informações adicionais para o mapa
        const flight = flights.find(f => f.id === flightEvent.flightId);
        
        if (flight) {
          // Log para debug mostrando que o voo foi encontrado
          console.log(`[QuotationStickyScroll] Voo encontrado para FlightEvent ${flightEvent.id}:`, {
            flightId: flight.id,
            airline: flight.airline,
            flightNumber: flight.flightNumber,
            fromPage: (flight as any)._page || 'Página desconhecida'
          });
          
          // Extrair coordenadas apropriadas com base em isDeparture
          const coordinates = flightEvent.isDeparture 
            ? flight.departureCoordinates 
            : flight.arrivalCoordinates;
            
          // Verificar se as coordenadas são válidas
          const hasValidCoordinates = coordinates && 
                                     typeof coordinates.lat === 'number' && 
                                     typeof coordinates.lng === 'number' &&
                                     !isNaN(coordinates.lat) && 
                                     !isNaN(coordinates.lng);
                                     
          if (!hasValidCoordinates) {
            console.warn(`[QuotationStickyScroll] Aviso: Coordenadas inválidas para FlightEvent ${flightEvent.id} (${flightEvent.isDeparture ? 'departure' : 'arrival'})`, {
              flightId: flight.id,
              coordinates
            });
          }
          
          // Buscar o horário e local corretos
          const dateTime = flightEvent.isDeparture
            ? flight.departureDateTime
            : flight.arrivalDateTime;
          
          const location = flightEvent.isDeparture
            ? flight.departureAirport
            : flight.arrivalAirport;
          
          // Para chegadas com conexões, usar informações da última conexão
          if (!flightEvent.isDeparture && flight.connections && flight.connections.length > 0) {
            const lastConnection = flight.connections[flight.connections.length - 1];
            
            // Enriquecer com informações para display no mapa
            const enrichedFlightEvent = {
              ...flightEvent,
              _displayDates: lastConnection.arrivalDateTime ? `${lastConnection.arrivalDateTime.split('T')[0].split('-').reverse().join('/')}` : undefined,
              _displayTime: lastConnection.arrivalDateTime ? lastConnection.arrivalDateTime.split('T')[1]?.substring(0, 5) : undefined,
              _displayLocation: lastConnection.arrivalAirport,
              _coordinates: hasValidCoordinates ? coordinates : null,  // Usar coordenadas apenas se forem válidas
              coordinates: hasValidCoordinates ? coordinates : null    // Adicionar também ao campo normal de coordenadas
            };
            
            newItems.push({
              id: flightEvent.id,
              type: flightEvent.isDeparture ? 'flight-departure' : 'flight-arrival',
              globalOrder: flightEvent.globalOrder ?? 999999,
              item: enrichedFlightEvent,
              flightEvent: enrichedFlightEvent,  // Adicionando o objeto também no campo flightEvent para compatibilidade
              flightId: flightEvent.flightId     // Persistir o flightId para garantir a associação
            });
            
            console.log(`[QuotationStickyScroll] FlightEvent ID=${enrichedFlightEvent.id} enriquecido com coordenadas:`, {
              _coordinates: enrichedFlightEvent._coordinates,
              _displayDates: enrichedFlightEvent._displayDates,
              _displayTime: enrichedFlightEvent._displayTime
            });
          } else {
            // Para partidas ou chegadas sem conexões
            const enrichedFlightEvent = {
              ...flightEvent,
              _displayDates: dateTime ? `${dateTime.split('T')[0].split('-').reverse().join('/')}` : undefined,
              _displayTime: dateTime ? dateTime.split('T')[1]?.substring(0, 5) : undefined,
              _displayLocation: location,
              _coordinates: hasValidCoordinates ? coordinates : null,  // Usar coordenadas apenas se forem válidas
              coordinates: hasValidCoordinates ? coordinates : null    // Adicionar também ao campo normal de coordenadas
            };
            
            newItems.push({
              id: flightEvent.id,
              type: flightEvent.isDeparture ? 'flight-departure' : 'flight-arrival',
              globalOrder: flightEvent.globalOrder ?? 999999,
              item: enrichedFlightEvent,
              flightEvent: enrichedFlightEvent,  // Adicionando o objeto também no campo flightEvent para compatibilidade
              flightId: flightEvent.flightId     // Persistir o flightId para garantir a associação
            });
            
            console.log(`[QuotationStickyScroll] FlightEvent ID=${enrichedFlightEvent.id} enriquecido com coordenadas:`, {
              _coordinates: enrichedFlightEvent._coordinates,
              _displayDates: enrichedFlightEvent._displayDates,
              _displayTime: enrichedFlightEvent._displayTime
            });
          }
        } else {
          // Adicionar mesmo sem encontrar o voo (a lógica de fallback no mapa vai lidar com isso)
          console.warn(`[QuotationStickyScroll] Aviso: FlightEvent ID=${flightEvent.id} sem voo associado encontrado. flightId=${flightEvent.flightId}`);
          
          // Mesmo sem encontrar o voo, ainda adicionar ao array de itens com coordenadas nulas
          newItems.push({
            id: flightEvent.id,
            type: flightEvent.isDeparture ? 'flight-departure' : 'flight-arrival',
            globalOrder: flightEvent.globalOrder ?? 999999,
            item: {
              ...flightEvent,
              _coordinates: null,
              coordinates: null
            },
            flightEvent: {
              ...flightEvent,
              _coordinates: null,
              coordinates: null
            },
            flightId: flightEvent.flightId
          });
        }
      });
    }
    
    // Ordenar por globalOrder
    newItems.sort((a, b) => a.globalOrder - b.globalOrder);
    
    // Log the final sorted items
    console.log(`[QuotationStickyScroll] Final sorted unifiedItems:`, 
      newItems.map((item, index) => ({
        index,
        id: item.id,
        type: item.type,
        globalOrder: item.globalOrder,
        accommodationId: item.type.includes('accommodation') ? item.accommodationId : undefined,
        flightId: item.type.includes('flight') ? item.flightId : undefined
      }))
    );
    
    // Atualizar os itens unificados
    setUnifiedItems(newItems);
    
    // Reset refs array para o novo número de itens
    textSectionsRef.current = textSectionsRef.current.slice(0, newItems.length);
    
    // Resetar os índices de imagem para os novos itens
    setCurrentImageIndices(new Array(newItems.length).fill(0));
    
    // Garantir que a seção introdutória esteja ativa inicialmente
    setActiveIndex(-1);
    // Garantir que o mapa seja inicializado após mudanças nos dados
    setMapInitialized(true);
    
  }, [blocks, tours, transportations, restaurants, scrollId, componentId, accommodations, showAccommodations, flights, flightEvents, accommodationEvents]); // Adicionar flights à lista de dependências

  // Função dedicada à inicialização do mapa que será chamada explicitamente
  const initializeMap = useCallback(() => {
    setMapInitialized(true);
  }, [blocks, componentId]);

  // Efeito para garantir que o mapa seja inicializado quando a seção introdutória estiver ativa
  // ou quando o componente for montado
  useEffect(() => {
    // Garantir que o mapa seja inicializado imediatamente
    if (!mapInitialized) {
      initializeMap();
    }
  }, [mapInitialized, initializeMap, componentId, activeIndex]);

  // Efeito para inicializar explicitamente quando o componente montar
  useEffect(() => {
    // Certificar-se de que o mapa será inicializado na montagem
    initializeMap();
    
    // Também certificar-se de que será inicializado após um tempo curto
    // para garantir que o DOM está pronto
    const timer = setTimeout(() => {
      initializeMap();
    }, 200);
    
    return () => clearTimeout(timer);
  }, [initializeMap]);

  // Reset refs array when blocks, tours, or transportations change
  useEffect(() => {
    // Ajustar o array de referências para acomodar todos os blocos, tours e transportations
    textSectionsRef.current = textSectionsRef.current.slice(0, unifiedItems.length);
    
    // Resetar os índices de imagem quando os blocos, tours ou transportations mudarem
    setCurrentImageIndices(new Array(unifiedItems.length).fill(0));
    
    // Garantir que a seção introdutória esteja ativa inicialmente
    setActiveIndex(-1);
    // Resetar o estado de inicialização do mapa quando os itens mudarem
    setMapInitialized(false);
    
    // Forçar a reinicialização do mapa
    return () => {
    };
  }, [blocks, tours, transportations, restaurants, componentId, unifiedItems]);

  // Set up scroll event to detect which text section is in view
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      // Chamar o callback onScrollStart na primeira rolagem
      if (onScrollStart && !hasCalledScrollStart.current && container.scrollTop > 10) {
        onScrollStart();
        hasCalledScrollStart.current = true;
      }
      
      const { scrollTop, clientHeight } = container;
      
      // Verificar se a seção introdutória está visível
      if (introSectionRef.current && showMap) {
        const introRect = introSectionRef.current.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // Calcular a posição relativa da seção introdutória em relação ao contêiner
        const introTop = introRect.top - containerRect.top;
        const introBottom = introRect.bottom - containerRect.top;
        
        // Considerar a seção introdutória ativa se estiver substancialmente visível
        if (scrollTop < 20 || (introTop < clientHeight * 0.4 && introBottom > clientHeight * 0.3)) {
          if (activeIndex !== -1) {
            setActiveIndex(-1);
          }
          return;
        }
      }

          // Find which section has crossed the middle of the viewport
  let newActiveIndex = -1; // Default to intro section

  textSectionsRef.current.forEach((ref, index) => {
    if (!ref) return;
    
    const rect = ref.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    // Calcular a posição da seção em relação ao contêiner
    const refTop = rect.top - containerRect.top;
    const refBottom = rect.bottom - containerRect.top;
    
    // Calcular o ponto médio da viewport
    const viewportMiddle = clientHeight / 2;
    
    // Verificar se o topo da seção cruzou a metade da tela
    // Uma seção é considerada ativa quando seu topo está na metade superior da tela
    if (refTop <= viewportMiddle && refBottom > viewportMiddle) {
      newActiveIndex = index;
    }
    // Se nenhuma seção cruzou a metade, manter o último índice ativo
    // ou usar o primeiro se for a primeira vez
  });

  // Se nenhuma seção cruzou a metade, verificar se devemos ativar o primeiro item
  if (newActiveIndex === -1) {
    const containerRect = container.getBoundingClientRect();
    const firstRef = textSectionsRef.current[0];
    const lastRef = textSectionsRef.current[textSectionsRef.current.length - 1];

    // Se já passamos por todos os itens (fim da lista), não mostrar nenhuma imagem
    if (lastRef) {
      const lastRect = lastRef.getBoundingClientRect();
      const lastRefBottom = lastRect.bottom - containerRect.top;
      if (lastRefBottom <= 0) {
        newActiveIndex = -2; // Nenhum item visível
      }
    }

    // Se ainda não definimos, tratar o topo (antes do primeiro item)
    if (newActiveIndex === -1 && firstRef) {
      const firstRect = firstRef.getBoundingClientRect();
      const firstRefTop = firstRect.top - containerRect.top;

      // Se o primeiro item ainda não chegou à metade, manter na seção introdutória
      if (firstRefTop > clientHeight / 2) {
        newActiveIndex = -1; // Seção introdutória
      } else {
        // Só ativa o primeiro item se ele estiver pelo menos parcialmente visível
        const firstRefBottom = firstRect.bottom - containerRect.top;
        if (firstRefBottom > 0) {
          newActiveIndex = 0; // Ativar o primeiro item
        } else {
          newActiveIndex = -2; // Nenhum item visível
        }
      }
    }
  }

  // Só atualizar o índice ativo se for diferente do atual
  if (activeIndex !== newActiveIndex) {
    setActiveIndex(newActiveIndex);
  }
    };

    container.addEventListener('scroll', handleScroll);
    
    // Executar a verificação inicial após um pequeno atraso para garantir que os elementos estejam renderizados
    setTimeout(() => {
      handleScroll();
    }, 100);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [blocks.length, activeIndex, onScrollStart, showMap]);

  // Reset the scroll start flag when blocks change
  useEffect(() => {
    hasCalledScrollStart.current = false;
  }, [blocks]);

  // Função para rolar até um bloco específico
  const scrollToBlock = (index: number) => {
    if (index === -1 && introSectionRef.current) {
      introSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    } else if (textSectionsRef.current[index]) {
      textSectionsRef.current[index]?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Função para navegar para a próxima imagem de um item
  const nextImage = (itemIndex: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Impedir que o evento se propague
    
    if (itemIndex < 0 || itemIndex >= unifiedItems.length) return;
    
    const item = unifiedItems[itemIndex].item;
    // Verificar se existem imagens para navegar
    if (!('images' in item) || !item.images || item.images.length <= 1) return;
    
    setCurrentImageIndices(prev => {
      const newIndices = [...prev];
      newIndices[itemIndex] = (newIndices[itemIndex] + 1) % item.images!.length;
      return newIndices;
    });
  };

  // Função para navegar para a imagem anterior de um item
  const prevImage = (itemIndex: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Impedir que o evento se propague
    
    if (itemIndex < 0 || itemIndex >= unifiedItems.length) return;
    
    const item = unifiedItems[itemIndex].item;
    // Verificar se existem imagens para navegar
    if (!('images' in item) || !item.images || item.images.length <= 1) return;
    
    setCurrentImageIndices(prev => {
      const newIndices = [...prev];
      newIndices[itemIndex] = (newIndices[itemIndex] - 1 + item.images!.length) % item.images!.length;
      return newIndices;
    });
  };

  // Formatar data se ela existir
  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '';
    
    try {
      // Converter para Date se for string
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      
      // Extrair apenas ano, mês e dia para evitar problemas de timezone
      const localDate = new Date(
        dateObj.getFullYear(),
        dateObj.getMonth(),
        dateObj.getDate()
      );
      
      return format(localDate, "dd/MM/yyyy", { locale: ptBR });
    } catch (e) {
    return formatSafeDate(date, "dd/MM/yyyy");
    }
  };

  return (
    <div className="w-full h-[calc(100vh-120px)] overflow-hidden" id={componentId.current}>
      <div 
        ref={containerRef}
        className="h-full flex flex-col lg:flex-row overflow-y-auto"
      >
        {/* Text sections - scrollable */}
        <div className="lg:w-1/2 p-6 lg:p-8 space-y-20">
          {/* Seção introdutória com lista de itens - mostrar apenas se showMap for true */}
          {showMap ? (
            <div 
              ref={introSectionRef}
              className={`transition-opacity duration-300 text-left ${
                activeIndex === -1 ? "opacity-100" : "opacity-40"
              }`}
            >
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-voah-teal">
                  Visão Geral
                </h2>
              </div>
              
              <div className="space-y-2">
                {/* Lista de itens unificados */}
                {unifiedItems.map((item, index) => {
                  // Determinar o tipo de conteúdo com base no tipo do item
                  let title: string = '';
                  let location: string | undefined = undefined;
                  let dates: string | undefined = undefined;
                  let time: string | undefined = undefined;
                  let description: string | undefined = undefined;
                  let notes: string | undefined = undefined;
                  let dateInfo: React.ReactNode = null;
                  let additionalInfo: React.ReactNode = null;
                  
                  if (item.type === 'block') {
                    // Adicionar suporte para blocos
                    const block = item.item as QuotationBlock;
                    title = block.title || `Bloco ${index + 1}`;
                    location = block.location || '';
                    description = block.description || '';
                    notes = block.notes || '';
                    dateInfo = (
                      <div className="text-sm text-gray-500 mt-1">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {block.endDate && block.startDate
                              ? `${formatDate(block.startDate)} - ${formatDate(block.endDate)}`
                              : formatDate(block.startDate)}
                          </span>
                        </div>
                      </div>
                    );
                  } else if (item.type === 'tour') {
                    const tour = item.item as TourInfo;
                    title = tour.name || `Passeio ${index + 1}`;
                    location = tour.location;
                  } else if (item.type === 'transportation') {
                    const transportation = item.item as TransportationInfo;
                    title = transportation.name || `Transporte ${index + 1}`;
                    location = transportation.location;
                    time = transportation.time;
                  } else if (item.type === 'restaurant') {
                    const restaurant = item.item as RestaurantInfo;
                    title = restaurant.name || `Restaurante ${index + 1}`;
                    location = restaurant.location;
                  } else if (item.type === 'accommodation' || item.type === 'accommodation-checkin' || item.type === 'accommodation-checkout') {
                    // Tratamento unificado para acomodações
                    const accommodation = item.item as AccommodationInfo;
                    
                    // Adicionar prefixo "Check-in: " ou "Check-out: " quando aplicável
                    if (item.type === 'accommodation-checkin') {
                      title = `Check-in: ${accommodation.name || `Hospedagem ${index + 1}`}`;
                    } else if (item.type === 'accommodation-checkout') {
                      title = `Check-out: ${accommodation.name || `Hospedagem ${index + 1}`}`;
                    } else {
                    title = accommodation.name || `Hospedagem ${index + 1}`;
                    }
                    
                    location = accommodation.location || '';
                    description = accommodation.description || '';
                    
                    // Informações adicionais específicas para acomodações
                    if (accommodation.checkInDate || accommodation.checkOutDate) {
                      dateInfo = (
                        <div className="text-sm text-gray-500 mt-1">
                          <div className="flex flex-col">
                            {accommodation.checkInDate && (
                              <div className="flex items-center">
                                <Calendar className="h-3.5 w-3.5 mr-1 text-gray-500" />
                                <span className="text-sm font-medium">
                                  Check-in: {format(new Date(accommodation.checkInDate), "dd/MM/yyyy")}
                                  {accommodation.checkInTime && ` às ${accommodation.checkInTime}`}
                                </span>
                              </div>
                            )}
                            {accommodation.checkOutDate && (
                              <div className="flex items-center mt-1">
                                <Calendar className="h-3.5 w-3.5 mr-1 text-gray-500" />
                                <span className="text-sm font-medium">
                                  Check-out: {format(new Date(accommodation.checkOutDate), "dd/MM/yyyy")}
                                  {accommodation.checkOutTime && ` às ${accommodation.checkOutTime}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                  } else if (item.type === 'flight-departure' || item.type === 'flight-arrival') {
                    if (item.flightEvent && 'isDeparture' in item.flightEvent) {
                      // This is an enriched FlightEvent that was already processed
                      const isDeparture = item.flightEvent.isDeparture;
                      
                      // Look up the associated flight to get the flight number
                      const flight = flights.find(f => f.id === item.flightEvent.flightId);
                      let flightNumber = '';
                      
                      if (flight) {
                        if (isDeparture) {
                          flightNumber = flight.flightNumber || '';
                        } else if (flight.connections && flight.connections.length > 0) {
                          flightNumber = flight.connections[flight.connections.length - 1].flightNumber || '';
                        } else {
                          flightNumber = flight.flightNumber || '';
                        }
                      }
                      
                      title = isDeparture ? `Partida, ${flightNumber}` : `Chegada, ${flightNumber}`;
                      
                      // Use the pre-calculated display properties if they exist
                      if ('_displayDates' in item.flightEvent) {
                        dates = item.flightEvent._displayDates || '';
                      }
                      
                      if ('_displayTime' in item.flightEvent) {
                        time = item.flightEvent._displayTime || '';
                      }
                    } else {
                      // Standard FlightInfo processing for backward compatibility
                    const flight = item.item as FlightInfo;
                    const hasConnections = flight.connections && flight.connections.length > 0;
                    const lastConnection = hasConnections ? flight.connections[flight.connections.length - 1] : null;
                    
                    if (item.type === 'flight-departure') {
                        title = `Partida, ${flight.flightNumber || ''}`;
                      location = flight.departureAirport || '';
                      dates = flight.departureDateTime ? formatDate(flight.departureDateTime) : '';
                      time = flight.departureDateTime ? flight.departureDateTime.split('T')[1]?.substring(0, 5) : '';
                    } else { // flight-arrival
                      if (hasConnections && lastConnection) {
                          title = `Chegada, ${lastConnection.flightNumber || ''}`;
                        location = lastConnection.arrivalAirport || '';
                        dates = lastConnection.arrivalDateTime ? formatDate(lastConnection.arrivalDateTime) : '';
                        time = lastConnection.arrivalDateTime ? lastConnection.arrivalDateTime.split('T')[1]?.substring(0, 5) : '';
                      } else {
                          title = `Chegada, ${flight.flightNumber || ''}`;
                        location = flight.arrivalAirport || '';
                        dates = flight.arrivalDateTime ? formatDate(flight.arrivalDateTime) : '';
                        time = flight.arrivalDateTime ? flight.arrivalDateTime.split('T')[1]?.substring(0, 5) : '';
                        }
                      }
                    }
                  }
                  
                  return (
                    <button
                      key={`item-${item.id}`}
                      onClick={() => scrollToBlock(index)}
                      onMouseEnter={() => setHoveredItemIndex(index)}
                      onMouseLeave={() => setHoveredItemIndex(-2)}
                      className={`w-full text-left p-3 rounded-md flex items-center justify-between transition-colors ${
                        activeIndex === index 
                          ? "bg-voah-teal/10 text-voah-teal" 
                          : "hover:bg-gray-100 text-gray-700"
                      }`}
                    >
                      <div className="flex items-center">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center mr-3 ${
                          activeIndex === index ? "bg-voah-teal text-white" : "bg-gray-200 text-gray-700"
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium">
                            {title}
                          </div>
                          {/* Combined date and time display */}
                          {(dates || time) && (
                            <div className="text-xs text-gray-500 flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {dates ? dates : ""}
                              {dates && time && <span className="mx-1">•</span>}
                              {time && (
                                <>
                                  <Clock className="h-3 w-3 mx-1" />
                                  {time}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight className={`h-4 w-4 ${
                        activeIndex === index ? "text-voah-teal" : "text-gray-400"
                      }`} />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            // Adicionar espaço em branco quando showMap for false para que o primeiro item fique posicionado no meio
            <div className="h-[20vh]" aria-hidden="true"></div>
          )}
          
          {/* Conteúdo de cada item - text sections */}
          {unifiedItems.map((unifiedItem, index) => {
            const item = unifiedItem.item;
            
            // Renderizar o conteúdo adequado com base no tipo do item
            let title = '';
            let location = '';
            let description = '';
            let notes = '';
            let dateInfo = null;
            let additionalInfo = null;
            
            if (unifiedItem.type === 'block') {
              const block = item as QuotationBlock;
              title = block.title || `Bloco ${index + 1}`;
              location = block.location || '';
              description = block.description || '';
              notes = block.notes || '';
              dateInfo = (
                <div className="text-sm text-gray-500 mt-1">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {block.endDate && block.startDate
                        ? `${formatDate(block.startDate)} - ${formatDate(block.endDate)}`
                        : formatDate(block.startDate)}
                    </span>
                  </div>
                </div>
              );
            } else if (unifiedItem.type === 'accommodation' || unifiedItem.type === 'accommodation-checkin' || unifiedItem.type === 'accommodation-checkout') {
              console.log(`[QuotationStickyScroll] Rendering accommodation item:`, {
                id: unifiedItem.id,
                type: unifiedItem.type,
                accommodationId: unifiedItem.accommodationId,
                itemId: (item as AccommodationInfo).id,
                name: (item as AccommodationInfo).name,
                hasAccommodation: !!unifiedItem.accommodation
              });
              
              return (
                <div 
                  key={`item-details-${unifiedItem.id}`}
                  ref={el => textSectionsRef.current[index] = el}
                  className={`transition-opacity duration-300 text-left ${
                    activeIndex === index ? "opacity-100" : "opacity-40"
                  }`}
                >
                  {/* Novo card para eventos de acomodação com diferenciação visual clara */}
                  <div className="border border-gray-200 rounded-md p-5 shadow-sm">
                    {/* Cabeçalho com ícone e tipo de evento */}
                    <div className={`flex items-center ${isAccommodationCheckin(unifiedItem) ? "text-emerald-600" : "text-amber-600"}`}>
                      <div className="mr-3 bg-gray-100 rounded-full p-2">
                        {isAccommodationCheckin(unifiedItem) ? (
                          <ArrowRight className="h-5 w-5" /> // Ícone de entrada para check-in
                        ) : (
                          <X className="h-5 w-5" /> // Ícone de saída para check-out
                        )}
                      </div>
                      <h3 className="text-lg font-semibold">
                        {isAccommodationCheckin(unifiedItem) ? "Check-in" : "Check-out"}
                      </h3>
                    </div>

                    {/* Informações da hospedagem */}
                    <div className="mt-4">
                      <h4 className="font-medium text-gray-800">{(item as AccommodationInfo).name}</h4>
                      
                      {/* Informações de data e horário */}
                      <div className="mt-3 space-y-2">
                        {isAccommodationCheckin(unifiedItem) && unifiedItem.accommodation?.checkInDate && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                            <span>Data: {formatDate(unifiedItem.accommodation.checkInDate)}</span>
                          </div>
                        )}
                        
                        {isAccommodationCheckout(unifiedItem) && unifiedItem.accommodation?.checkOutDate && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                            <span>Data: {formatDate(unifiedItem.accommodation.checkOutDate)}</span>
                          </div>
                        )}
                        
                        {isAccommodationCheckin(unifiedItem) && unifiedItem.accommodation?.checkInTime && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="h-4 w-4 mr-2 text-gray-500" />
                            <span>Horário: {unifiedItem.accommodation.checkInTime}</span>
                          </div>
                        )}
                        
                        {isAccommodationCheckout(unifiedItem) && unifiedItem.accommodation?.checkOutTime && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="h-4 w-4 mr-2 text-gray-500" />
                            <span>Horário: {unifiedItem.accommodation.checkOutTime}</span>
                          </div>
                        )}
                        
                    {(item as AccommodationInfo).location && (
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                        <span>{(item as AccommodationInfo).location}</span>
                </div>
                    )}
                      </div>
                      
                      {/* Notas do evento, se existirem */}
                      {(item as AccommodationEvent).notes && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-md text-sm text-gray-700">
                          <div className="flex items-start">
                            <Info className="h-4 w-4 mr-2 mt-0.5 text-gray-500" />
                            <div>{(item as AccommodationEvent).notes}</div>
                          </div>
                        </div>
                      )}
                      
                      {/* Botão para ver detalhes */}
                      {unifiedItem.accommodationId && onViewAccommodationDetails && (
                      <button 
                          onClick={() => onViewAccommodationDetails(unifiedItem.accommodationId!)}
                          className="mt-4 text-sm text-voah-teal hover:text-voah-teal-dark flex items-center transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" />
                          Ver detalhes da hospedagem
                      </button>
                    )}
                    </div>
                  </div>
                </div>
              );
            } else if (unifiedItem.type === 'tour') {
              const tour = item as TourInfo;
              title = tour.name || `Passeio ${index + 1}`;
              location = tour.location || '';
              description = tour.description || '';
              notes = tour.notes || '';
            } else if (unifiedItem.type === 'transportation') {
              const transportation = item as TransportationInfo;
              title = transportation.name || `Transporte ${index + 1}`;
              location = transportation.location || '';
              description = transportation.description || '';
              
              // Adicionar dateInfo para mostrar horário de saída e duração
              if (transportation.time || transportation.duration) {
                dateInfo = (
                  <div className="text-sm text-gray-500 mt-1">
                    <div className="flex flex-col">
                      {transportation.time && (
                        <div className="flex items-center">
                          <Clock className="h-3.5 w-3.5 mr-1 text-gray-500" />
                          <span className="text-sm font-medium">Horário: {transportation.time}</span>
                        </div>
                      )}
                      {transportation.duration && (
                        <div className="flex items-center mt-1">
                          <Clock className="h-3.5 w-3.5 mr-1 text-gray-500" />
                          <span className="text-sm font-medium">Duração: {transportation.duration}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              
              // Adicionar informações importantes se existirem
              if (transportation.importantInfo && transportation.importantInfo.trim() !== '') {
                additionalInfo = (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <div className="flex items-start gap-2">
                      <Info className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-800 mb-1">Informações Importantes:</div>
                        <div className="text-[15px] text-gray-700">{transportation.importantInfo}</div>
                      </div>
                    </div>
                  </div>
                );
              }
            } else if (unifiedItem.type === 'restaurant') {
              const restaurant = item as RestaurantInfo;
              title = restaurant.name || `Restaurante ${index + 1}`;
              location = restaurant.location || '';
              description = restaurant.description || '';
              
              // Informações adicionais específicas para restaurantes
              if (restaurant.cuisineType || restaurant.menuHighlights || restaurant.notes) {
                additionalInfo = (
                  <>
                    {restaurant.cuisineType && (
                      <div className="mt-3 mb-2">
                        <div className="text-sm font-medium text-gray-700">Tipo de Cozinha:</div>
                        <div className="text-[15px] text-gray-600">{restaurant.cuisineType}</div>
                      </div>
                    )}
                    
                    {restaurant.menuHighlights && (
                      <div className="mt-3 mb-2">
                        <div className="text-sm font-medium text-gray-700">Destaques do Menu:</div>
                        <div className="text-[15px] text-gray-600">{restaurant.menuHighlights}</div>
                      </div>
                    )}
                    
                    {/* Exibir as observações da mesma forma que importantInfo dos transportes */}
                    {restaurant.notes && restaurant.notes.trim() !== '' && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <div className="flex items-start gap-2">
                          <Info className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium text-gray-800 mb-1">Observações:</div>
                            <div className="text-[15px] text-gray-700">{restaurant.notes}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              }
            } else if (unifiedItem.type === 'flight-departure' || unifiedItem.type === 'flight-arrival') {
              return (
                <div 
                  key={`item-details-${unifiedItem.id}`}
                  ref={el => textSectionsRef.current[index] = el}
                  className={`transition-opacity duration-300 text-left ${
                    activeIndex === index ? "opacity-100" : "opacity-40"
                  }`}
                >
                  <SimpleFlightEventItem 
                    flight={item as FlightInfo} 
                    eventType={unifiedItem.type === 'flight-departure' ? 'departure' : 'arrival'} 
                    onViewDetails={onViewFlightDetails}
                  />
                </div>
              );
            }
            
            return (
              <div 
                key={`item-details-${unifiedItem.id}`}
                ref={el => textSectionsRef.current[index] = el}
                className={`transition-opacity duration-300 text-left ${
                  activeIndex === index ? "opacity-100" : "opacity-40"
                }`}
              >
                <div className="mb-4">
                  <h2 
                    className="text-2xl font-bold text-voah-teal hover:underline cursor-pointer"
                    onClick={() => scrollToBlock(-1)}
                  >
                    {title}
                  </h2>
                  
                  {/* Links para Google Maps */}
                  {unifiedItem.type === 'transportation' && (
                    <>
                      {/* Link para o local de partida */}
                      {((item as TransportationInfo).departureGoogleMapsUrl || 
                        (item as TransportationInfo).departurePlaceId || 
                        (item as TransportationInfo).departureCoordinates) && (
                        <button 
                          onClick={() => openInGoogleMaps({
                            ...(item as TransportationInfo), 
                            googleMapsUrl: (item as TransportationInfo).departureGoogleMapsUrl, 
                            placeId: (item as TransportationInfo).departurePlaceId, 
                            coordinates: (item as TransportationInfo).departureCoordinates
                          })}
                          className="flex items-center text-voah-teal text-sm mt-1 hover:underline cursor-pointer w-fit"
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver local de partida no Google Maps
                        </button>
                      )}
                      
                      {/* Link para o local de chegada */}
                      {((item as TransportationInfo).arrivalGoogleMapsUrl || 
                        (item as TransportationInfo).arrivalPlaceId || 
                        (item as TransportationInfo).arrivalCoordinates) && (
                        <button 
                          onClick={() => openInGoogleMaps({
                            ...(item as TransportationInfo), 
                            googleMapsUrl: (item as TransportationInfo).arrivalGoogleMapsUrl, 
                            placeId: (item as TransportationInfo).arrivalPlaceId, 
                            coordinates: (item as TransportationInfo).arrivalCoordinates
                          })}
                          className="flex items-center text-voah-teal text-sm mt-1 hover:underline cursor-pointer w-fit"
                        >
                          <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver local de chegada no Google Maps
                        </button>
                      )}
                    </>
                  )}
                  
                  {/* Link genérico para Google Maps (para outros tipos de itens ou transportes sem info específica) */}
                  {(unifiedItem.type !== 'transportation' || 
                    (!((item as TransportationInfo).departureGoogleMapsUrl || 
                      (item as TransportationInfo).departurePlaceId || 
                      (item as TransportationInfo).departureCoordinates) && 
                    !((item as TransportationInfo).arrivalGoogleMapsUrl || 
                      (item as TransportationInfo).arrivalPlaceId || 
                      (item as TransportationInfo).arrivalCoordinates))) && 
                    createGoogleMapsUrl(item) && (
                    <button 
                      onClick={() => openInGoogleMaps(item)}
                      className="flex items-center text-voah-teal text-sm mt-1 hover:underline cursor-pointer w-fit"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver no Google Maps
                    </button>
                  )}
                  
                  {dateInfo}
                </div>
                
                {(description || notes) && (
                  <div 
                    className="text-[16px] text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ 
                      __html: (description || notes)
                        .replace(/style="[^"]*"/g, '')
                        .replace(/style='[^']*'/g, '')
                    }}
                  />
                )}
                
                {additionalInfo}
              </div>
            );
          })}
          
          {/* Adicionar espaço em branco maior ao final para permitir que os últimos itens sejam ativados */}
          <div className="h-[80vh]" aria-hidden="true"></div>
        </div>

        {/* Image/Map section - sticky on desktop, fixed on bottom for mobile */}
        <div className="hidden lg:block lg:w-1/2 sticky top-0 bottom-0 h-[calc(100vh-120px)] mt-4">
          {/* Mapa para a seção introdutória - mostrar apenas se showMap for true */}
          {showMap && (
            <div 
              className={`absolute inset-0 transition-opacity duration-500 ${
                activeIndex === -1 ? "opacity-100 z-10" : "opacity-0 z-0"
              }`}
            >
              <div className="relative h-full w-full group">
                {/* Remover a condição activeIndex === -1 para garantir que o mapa seja sempre renderizado */}
                {mapInitialized && <QuotationMap 
                  blocks={blocks} 
                  tours={tours}
                  transportations={transportations}
                  restaurants={restaurants}
                  accommodations={accommodations}
                  flights={flights}
                  flightEvents={flightEvents}
                  accommodationEvents={accommodationEvents}
                  unifiedItems={unifiedItems}
                  showMapLines={showMapLines} 
                  hoveredItemIndex={hoveredItemIndex} 
                />}
                
                {/* Botão de expansão - aparece apenas no hover */}
                <button
                  onClick={() => setMapModalOpen(true)}
                  className="absolute top-10 right-10 bg-[#F97316] text-white rounded-full p-1.5 opacity-0 group-hover:opacity-90 transition-opacity hover:bg-[#EA580C] hover:opacity-100 z-20 shadow-md"
                  aria-label="Expandir mapa"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Imagens para cada item */}
          {unifiedItems.map((unifiedItem, index) => {
            const item = unifiedItem.item;
            const hasImages = 'images' in item && item.images && item.images.length > 0;
            
            // Para itens de voo, nunca mostrar imagem, mesmo que tenha
            const isFlight = unifiedItem.type === 'flight-departure' || unifiedItem.type === 'flight-arrival';
            
            return (
              <div 
                key={`item-img-${unifiedItem.id}`}
                className={`absolute inset-0 transition-opacity duration-500 ${
                  activeIndex === index ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
              >
                {/* Cartão específico para voos na versão desktop */}
                {isFlight ? (
                  <div className="h-full w-full overflow-auto bg-white shadow-md mt-4">
                    {/* Flight header com info da companhia aérea */}
                    <div className="bg-voah-teal text-white p-4">
                      <div className="flex flex-col items-center justify-between">
                        <div className="w-full mb-3">
                          <div className="flex items-center justify-center">
                            <PlaneTakeoff className="h-6 w-6 mr-3" />
                            <h3 className="text-xl font-semibold text-center">
                              {(() => {
                                // Primeiro verificar se temos flightEvent e flightId
                                const flightEvent = unifiedItem.flightEvent;
                                const flightId = unifiedItem.flightId;
                                const flight = flightId ? flights.find(f => f.id === flightId) : null;
                                
                                // Se temos o objeto enriquecido com _displayLocation
                                if (flightEvent && '_displayLocation' in flightEvent) {
                                  return unifiedItem.type === 'flight-arrival'
                                    ? `Chegada: ${flightEvent._displayLocation || ''}`
                                    : `Partida: ${flightEvent._displayLocation || ''}`;
                                }
                                
                                // Caso contrário, usar o item diretamente
                                return unifiedItem.type === 'flight-arrival' 
                                ? `Chegada: ${(item as FlightInfo).arrivalAirport || ''}`
                                : unifiedItem.type === 'flight-departure'
                                ? `Partida: ${(item as FlightInfo).departureAirport || ''}`
                                  : `${(item as FlightInfo).departureAirport || ''} - ${(item as FlightInfo).arrivalAirport || ''}`;
                              })()}
                            </h3>
                          </div>
                          <p className="text-white/80 text-sm text-center mt-1">
                            {(() => {
                              // Primeiro verificar se temos flightEvent e flightId
                              const flightEvent = unifiedItem.flightEvent;
                              const flightId = unifiedItem.flightId;
                              const flight = flightId ? flights.find(f => f.id === flightId) : null;
                              
                              if (flight) {
                                if (unifiedItem.type === 'flight-arrival' && flight.connections && flight.connections.length > 0) {
                                  const lastConnection = flight.connections[flight.connections.length - 1];
                                  return `${lastConnection.airline || 'Companhia aérea não especificada'} ${lastConnection.flightNumber ? `• ${lastConnection.flightNumber}` : ''}`;
                                } else {
                                  return `${flight.airline || 'Companhia aérea não especificada'} ${flight.flightNumber ? `• ${flight.flightNumber}` : ''}`;
                                }
                              } else {
                                // Fallback para o comportamento anterior
                                return unifiedItem.type === 'flight-arrival' && (item as FlightInfo).connections && (item as FlightInfo).connections.length > 0
                              ? `${(item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].airline || 'Companhia aérea não especificada'} ${(item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].flightNumber ? `• ${(item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].flightNumber}` : ''}`
                                  : `${(item as FlightInfo).airline || 'Companhia aérea não especificada'} ${(item as FlightInfo).flightNumber ? `• ${(item as FlightInfo).flightNumber}` : ''}`;
                            }
                            })()}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Flight route visualization */}
                    <div className="p-6">
                      <div className="flex flex-col items-center justify-between mb-6">
                        {/* Departure information */}
                        <div className="text-center mb-4">
                          <div className="text-sm text-gray-500 mb-1">Partida</div>
                          <div className="text-2xl font-bold text-voah-teal">
                            {(() => {
                              // Verificar se temos flightEvent e flightId
                              const flightEvent = unifiedItem.flightEvent;
                              const flightId = unifiedItem.flightId;
                              const flight = flightId ? flights.find(f => f.id === flightId) : null;
                              
                              if (flight) {
                                if (unifiedItem.type === 'flight-arrival' && flight.connections && flight.connections.length > 0) {
                                  return flight.connections[flight.connections.length - 1].departureAirport || '';
                                } else {
                                  return flight.departureAirport || '';
                                }
                              } else {
                                // Fallback para o comportamento anterior
                                return unifiedItem.type === 'flight-departure' 
                              ? (item as FlightInfo).departureAirport 
                              : unifiedItem.type === 'flight-arrival' && (item as FlightInfo).connections && (item as FlightInfo).connections.length > 0 
                                ? (item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].departureAirport 
                                    : (item as FlightInfo).departureAirport;
                              }
                            })()}
                          </div>
                          {(() => {
                            // Verificar se temos flightEvent com dados de data/hora
                            const flightEvent = unifiedItem.flightEvent;
                            const flightId = unifiedItem.flightId;
                            const flight = flightId ? flights.find(f => f.id === flightId) : null;
                            
                            // Se for evento de chegada com conexões
                            if (unifiedItem.type === 'flight-arrival' && flight && flight.connections && flight.connections.length > 0) {
                              const lastConnection = flight.connections[flight.connections.length - 1];
                              if (lastConnection.departureDateTime) {
                                return (
                                <div className="text-sm text-gray-700">
                                    {`${lastConnection.departureDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                    <span className="mx-1">•</span>
                                    {lastConnection.departureDateTime.split('T')[1]?.substring(0, 5)}
                                  </div>
                                );
                              }
                            } 
                            // Se for evento de partida
                            else if (unifiedItem.type === 'flight-departure' && flight && flight.departureDateTime) {
                              return (
                                <div className="text-sm text-gray-700">
                                  {`${flight.departureDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                  <span className="mx-1">•</span>
                                  {flight.departureDateTime.split('T')[1]?.substring(0, 5)}
                                </div>
                              );
                            }
                            
                            // Fallback para o comportamento anterior
                            if (unifiedItem.type === 'flight-departure') {
                              return ((item as FlightInfo).departureDateTime && (
                                <div className="text-sm text-gray-700">
                                  {`${(item as FlightInfo).departureDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                  <span className="mx-1">•</span>
                                  {(item as FlightInfo).departureDateTime.split('T')[1]?.substring(0, 5)}
                                </div>
                              ));
                            } else if (unifiedItem.type === 'flight-arrival' && (item as FlightInfo).connections && (item as FlightInfo).connections.length > 0) {
                              return ((item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalDateTime && (
                                  <div className="text-sm text-gray-700">
                                  {`${(item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                    <span className="mx-1">•</span>
                                  {(item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalDateTime.split('T')[1]?.substring(0, 5)}
                                  </div>
                              ));
                            } else {
                              return ((item as FlightInfo).departureDateTime && (
                                  <div className="text-sm text-gray-700">
                                  {`${(item as FlightInfo).departureDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                    <span className="mx-1">•</span>
                                    {(item as FlightInfo).departureDateTime.split('T')[1]?.substring(0, 5)}
                                  </div>
                              ));
                            }
                          })()}
                          {(() => {
                            // Verificar terminal se temos flightEvent e flightId
                            const flightEvent = unifiedItem.flightEvent;
                            const flightId = unifiedItem.flightId;
                            const flight = flightId ? flights.find(f => f.id === flightId) : null;
                            
                            if (flight) {
                              if (unifiedItem.type === 'flight-departure' && flight.departureTerminal) {
                                return (
                                  <div className="text-xs text-gray-500 mt-1">Terminal {flight.departureTerminal}</div>
                                );
                              } else if (unifiedItem.type === 'flight-arrival' && flight.connections && flight.connections.length > 0) {
                                const lastConnection = flight.connections[flight.connections.length - 1];
                                if (lastConnection.arrivalTerminal) {
                                  return (
                                    <div className="text-xs text-gray-500 mt-1">Terminal {lastConnection.arrivalTerminal}</div>
                                  );
                                }
                              }
                            }
                            
                            // Fallback para comportamento anterior
                            if (unifiedItem.type === 'flight-departure') {
                              return ((item as FlightInfo).departureTerminal && (
                                <div className="text-xs text-gray-500 mt-1">Terminal {(item as FlightInfo).departureTerminal}</div>
                              ));
                            } else if (unifiedItem.type === 'flight-arrival' && (item as FlightInfo).connections && (item as FlightInfo).connections.length > 0) {
                              return ((item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalTerminal && (
                                  <div className="text-xs text-gray-500 mt-1">
                                  Terminal {(item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalTerminal}
                                  </div>
                              ));
                            } else {
                              return ((item as FlightInfo).departureTerminal && (
                                  <div className="text-xs text-gray-500 mt-1">Terminal {(item as FlightInfo).departureTerminal}</div>
                              ));
                          }
                          })()}
                        </div>
                    
                        {/* Flight path visualization */}
                        <div className="flex flex-col items-center justify-center py-2 mb-4">
                          <div className="relative w-40 flex items-center justify-center">
                            <div className="h-0.5 bg-gray-300 w-full absolute"></div>
                            <ArrowRight className="text-voah-teal z-10 h-6 w-6 bg-white rounded-full p-0.5 shadow-sm" />
                          </div>
                          <div className="text-sm text-gray-700 mt-2">
                            <span className="font-medium">Duração: </span>
                            {(() => {
                              // Verificar se temos flightEvent e flightId
                              const flightEvent = unifiedItem.flightEvent;
                              const flightId = unifiedItem.flightId;
                              const flight = flightId ? flights.find(f => f.id === flightId) : null;
                              
                              if (flight) {
                                if (unifiedItem.type === 'flight-arrival' && flight.connections && flight.connections.length > 0) {
                                  const lastConnection = flight.connections[flight.connections.length - 1];
                                  return lastConnection.flightDuration || 'Não especificada';
                                } else {
                                  return flight.flightDuration || 'Não especificada';
                                }
                              } else {
                                // Fallback para o comportamento anterior
                                return unifiedItem.type === 'flight-departure' 
                              ? (item as FlightInfo).flightDuration || 'Não especificada'
                              : unifiedItem.type === 'flight-arrival' && (item as FlightInfo).connections && (item as FlightInfo).connections.length > 0
                                ? (item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].flightDuration || 'Não especificada'
                                    : (item as FlightInfo).flightDuration || 'Não especificada';
                            }
                            })()}
                          </div>
                        </div>
                    
                        {/* Arrival information */}
                        <div className="text-center">
                          <div className="text-sm text-gray-500 mb-1">Chegada</div>
                          <div className="text-2xl font-bold text-voah-teal">
                            {(() => {
                              // Verificar se temos flightEvent e flightId
                              const flightEvent = unifiedItem.flightEvent;
                              const flightId = unifiedItem.flightId;
                              const flight = flightId ? flights.find(f => f.id === flightId) : null;
                              
                              if (flight) {
                                if (unifiedItem.type === 'flight-arrival' && flight.connections && flight.connections.length > 0) {
                                  const lastConnection = flight.connections[flight.connections.length - 1];
                                  return lastConnection.arrivalAirport || flight.arrivalAirport || '';
                                } else {
                                  return flight.arrivalAirport || '';
                                }
                              } else {
                                // Fallback para o comportamento anterior
                                return unifiedItem.type === 'flight-arrival' 
                              ? (item as FlightInfo).connections && (item as FlightInfo).connections.length > 0
                                ? (item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalAirport
                                : (item as FlightInfo).arrivalAirport
                                  : (item as FlightInfo).arrivalAirport;
                              }
                            })()}
                          </div>
                          
                          {(() => {
                            // Verificar se temos flightEvent com dados de data/hora
                            const flightEvent = unifiedItem.flightEvent;
                            const flightId = unifiedItem.flightId;
                            const flight = flightId ? flights.find(f => f.id === flightId) : null;
                            
                            // Verificar data de chegada para evento de chegada com conexões
                            if (unifiedItem.type === 'flight-arrival' && flight && flight.connections && flight.connections.length > 0) {
                              const lastConnection = flight.connections[flight.connections.length - 1];
                              if (lastConnection.arrivalDateTime) {
                                return (
                                  <div className="text-sm text-gray-700">
                                    {`${lastConnection.arrivalDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                    <span className="mx-1">•</span>
                                    {lastConnection.arrivalDateTime.split('T')[1]?.substring(0, 5)}
                                  </div>
                                );
                              }
                            } 
                            // Verificar data de chegada para evento normal
                            else if (flight && flight.arrivalDateTime) {
                              return (
                                <div className="text-sm text-gray-700">
                                  {`${flight.arrivalDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                  <span className="mx-1">•</span>
                                  {flight.arrivalDateTime.split('T')[1]?.substring(0, 5)}
                                </div>
                              );
                            }
                            
                            // Fallback para o comportamento anterior
                            if (unifiedItem.type === 'flight-arrival') {
                              if ((item as FlightInfo).connections && (item as FlightInfo).connections.length > 0) {
                                return ((item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalDateTime && (
                                  <div className="text-sm text-gray-700">
                                    {`${(item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                    <span className="mx-1">•</span>
                                    {(item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalDateTime.split('T')[1]?.substring(0, 5)}
                                  </div>
                                ));
                              } else {
                                return ((item as FlightInfo).arrivalDateTime && (
                                  <div className="text-sm text-gray-700">
                                    {`${(item as FlightInfo).arrivalDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                    <span className="mx-1">•</span>
                                    {(item as FlightInfo).arrivalDateTime.split('T')[1]?.substring(0, 5)}
                                  </div>
                                ));
                              }
                            } else {
                              return ((item as FlightInfo).arrivalDateTime && (
                                <div className="text-sm text-gray-700">
                                  {`${(item as FlightInfo).arrivalDateTime.split('T')[0].split('-').reverse().join('/')}`}
                                  <span className="mx-1">•</span>
                                  {(item as FlightInfo).arrivalDateTime.split('T')[1]?.substring(0, 5)}
                                </div>
                              ));
                            }
                          })()}
                          
                          {(() => {
                            // Verificar terminal de chegada
                            const flightEvent = unifiedItem.flightEvent;
                            const flightId = unifiedItem.flightId;
                            const flight = flightId ? flights.find(f => f.id === flightId) : null;
                            
                            if (flight) {
                              if (unifiedItem.type === 'flight-arrival' && flight.connections && flight.connections.length > 0) {
                                const lastConnection = flight.connections[flight.connections.length - 1];
                                if (lastConnection.arrivalTerminal) {
                                  return (
                                    <div className="text-xs text-gray-500 mt-1">Terminal {lastConnection.arrivalTerminal}</div>
                                  );
                                }
                              } else if (flight.arrivalTerminal) {
                                return (
                                  <div className="text-xs text-gray-500 mt-1">Terminal {flight.arrivalTerminal}</div>
                                );
                              }
                            }
                            
                            // Fallback para comportamento anterior
                            if (unifiedItem.type === 'flight-arrival') {
                              if ((item as FlightInfo).connections && (item as FlightInfo).connections.length > 0) {
                                return ((item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalTerminal && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Terminal {(item as FlightInfo).connections[(item as FlightInfo).connections.length - 1].arrivalTerminal}
                                  </div>
                                ));
                              } else {
                                return ((item as FlightInfo).arrivalTerminal && (
                                  <div className="text-xs text-gray-500 mt-1">Terminal {(item as FlightInfo).arrivalTerminal}</div>
                                ));
                              }
                            } else {
                              return ((item as FlightInfo).arrivalTerminal && (
                                <div className="text-xs text-gray-500 mt-1">Terminal {(item as FlightInfo).arrivalTerminal}</div>
                              ));
                          }
                          })()}
                        </div>
                      </div>
                      
                      {/* Botão para ver detalhes do voo */}
                      {onViewFlightDetails && (
                        <div className="flex justify-center">
                          <button
                            onClick={() => {
                              // Usar o flightId de unifiedItem quando disponível
                              const idToUse = unifiedItem.flightId || (item as FlightInfo).id;
                              onViewFlightDetails(idToUse);
                            }}
                            className="text-sm text-voah-teal hover:text-voah-teal-dark flex items-center transition-colors"
                          >
                            <ExternalLink className="h-3.5 w-3.5 mr-1" />
                            Ver detalhes completos
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : hasImages ? (
                  <div className="relative h-full py-8 pr-[1px] group">
                    <img 
                      src={(item as any).images[currentImageIndices[index]]} 
                      alt={'title' in item ? item.title : ('name' in item ? item.name : `Item ${index + 1}`)}
                      className="h-full w-full object-cover"
                    />
                    
                    {/* Indicador de número de imagens */}
                    {(item as any).images.length > 1 && (
                      <div className="absolute bottom-12 right-5 bg-[#fbf0e2] text-voah-teal text-sm px-3 py-1.5 rounded-full flex items-center opacity-90 shadow-md">
                        <Image className="h-4 w-4 mr-1.5" />
                        <span className="font-medium">{currentImageIndices[index] + 1}/{(item as any).images.length}</span>
                      </div>
                    )}
                    
                    {/* Botões de navegação - só aparecem quando há mais de uma imagem */}
                    {(item as any).images.length > 1 && (
                      <>
                        {/* Botão anterior */}
                        <button 
                          onClick={(e) => prevImage(index, e)}
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all group-hover:bg-[#fbf0e2] group-hover:text-voah-teal group-hover:p-2 group-hover:left-3"
                          aria-label="Imagem anterior"
                        >
                          <ChevronLeft className="h-4 w-4 transition-all group-hover:h-5 group-hover:w-5" />
                        </button>
                        
                        {/* Botão próximo */}
                        <button 
                          onClick={(e) => nextImage(index, e)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all group-hover:bg-[#fbf0e2] group-hover:text-voah-teal group-hover:p-2 group-hover:right-3"
                          aria-label="Próxima imagem"
                        >
                          <ChevronRight className="h-4 w-4 transition-all group-hover:h-5 group-hover:w-5" />
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="h-full w-full flex items-center justify-center bg-voah-teal text-white text-4xl font-bold py-8 pr-[1px]">
                    {index + 1}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Mobile version of the image/map - fixed at bottom */}
        <div className="lg:hidden fixed bottom-4 left-4 right-4 h-60 rounded-xl overflow-hidden shadow-xl z-50">
          {/* Mapa para a seção introdutória em mobile */}
          <div 
            className={`absolute inset-0 transition-opacity duration-500 ${
              activeIndex === -1 ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <div className="relative h-full group">
              {/* Remover a condição activeIndex === -1 para garantir que o mapa seja sempre renderizado */}
              {mapInitialized && <QuotationMap 
                blocks={blocks} 
                tours={tours}
                transportations={transportations}
                restaurants={restaurants}
                accommodations={accommodations}
                flights={flights}
                flightEvents={flightEvents}
                accommodationEvents={accommodationEvents}
                unifiedItems={unifiedItems}
                showMapLines={showMapLines} 
                hoveredItemIndex={hoveredItemIndex} 
              />}
              
              {/* Botão de expansão para mobile */}
              <button
                onClick={() => setMapModalOpen(true)}
                className="absolute top-2 right-2 bg-[#F97316] text-white rounded-full p-1.5 opacity-80 group-hover:opacity-100 transition-opacity hover:bg-[#EA580C] z-20 shadow-md"
                aria-label="Expandir mapa"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Imagens para cada item em mobile - usando a mesma lógica do desktop */}
          {unifiedItems.map((unifiedItem, index) => {
            const item = unifiedItem.item;
            const hasImages = 'images' in item && item.images && item.images.length > 0;
            const title = 'title' in item ? item.title : ('name' in item ? item.name : `Item ${index + 1}`);
            
            // Não renderizar se for um item de acomodação e showAccommodations for false
            // Isso garante o mesmo comportamento do desktop
            if ((unifiedItem.type === 'accommodation') && 
                !showAccommodations) {
              return null;
            }
            
            // Tratamento para accommodation (Unified view - MOBILE)
            if (unifiedItem.type === 'accommodation' || unifiedItem.type === 'accommodation-checkin' || unifiedItem.type === 'accommodation-checkout') {
              return (
                <div 
                  key={`mobile-item-${unifiedItem.id}`}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    activeIndex === index ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  {hasImages ? (
                    <div className="relative h-full group">
                      <img 
                        src={(item as any).images[currentImageIndices[index]]} 
                        alt={title}
                        className="h-full w-full object-cover"
                      />
                      
                      {/* Accommodation overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white">
                        <p className="font-medium text-sm">{title}</p>
                        {(item as AccommodationInfo).checkInDate && (item as AccommodationInfo).checkOutDate && (
                          <p className="text-xs">
                            {formatDate((item as AccommodationInfo).checkInDate)} - {formatDate((item as AccommodationInfo).checkOutDate)}
                          </p>
                        )}
                      </div>
                      
                      {/* Indicador de número de imagens */}
                      {(item as any).images.length > 1 && (
                        <div className="absolute bottom-12 right-5 bg-[#fbf0e2] text-voah-teal text-sm px-3 py-1.5 rounded-full flex items-center opacity-90 shadow-md">
                          <Image className="h-4 w-4 mr-1.5" />
                          <span className="font-medium">{currentImageIndices[index] + 1}/{(item as any).images.length}</span>
                        </div>
                      )}
                      
                      {/* Botões de navegação */}
                      {(item as any).images.length > 1 && (
                        <>
                          <button 
                            onClick={(e) => prevImage(index, e)}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all group-hover:bg-[#fbf0e2] group-hover:text-voah-teal group-hover:p-2 group-hover:left-3"
                            aria-label="Imagem anterior"
                          >
                            <ChevronLeft className="h-4 w-4 transition-all group-hover:h-5 group-hover:w-5" />
                          </button>
                          
                          <button 
                            onClick={(e) => nextImage(index, e)}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all group-hover:bg-[#fbf0e2] group-hover:text-voah-teal group-hover:p-2 group-hover:right-3"
                            aria-label="Próxima imagem"
                          >
                            <ChevronRight className="h-4 w-4 transition-all group-hover:h-5 group-hover:w-5" />
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center bg-green-600 text-white">
                      <div className="text-4xl font-bold">{index + 1}</div>
                      <div className="mt-2 px-4 text-center text-sm">{title}</div>
                      {(item as AccommodationInfo).checkInDate && (item as AccommodationInfo).checkOutDate && (
                        <div className="mt-1 text-xs">{formatDate((item as AccommodationInfo).checkInDate)} - {formatDate((item as AccommodationInfo).checkOutDate)}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            
            // Tratamento para transportes
            if (unifiedItem.type === 'transportation') {
              return (
                <div 
                  key={`mobile-item-${unifiedItem.id}`}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    activeIndex === index ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  {hasImages ? (
                    <div className="relative h-full group">
                      <img 
                        src={(item as any).images[currentImageIndices[index]]} 
                        alt={title}
                        className="h-full w-full object-cover"
                      />
                      
                      {/* Transportation overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white">
                        <p className="font-medium text-sm">{title}</p>
                        {(item as TransportationInfo).departureLocation && (item as TransportationInfo).arrivalLocation && (
                          <p className="text-xs">{(item as TransportationInfo).departureLocation} → {(item as TransportationInfo).arrivalLocation}</p>
                        )}
                        {(item as TransportationInfo).time && (
                          <p className="text-xs">Horário: {(item as TransportationInfo).time}</p>
                        )}
                        {(item as TransportationInfo).importantInfo && (
                          <p className="text-xs mt-1 bg-orange-600/80 p-1 rounded">
                            <Info className="h-3 w-3 inline-block mr-1" />
                            {(item as TransportationInfo).importantInfo}
                          </p>
                        )}
                      </div>
                      
                      {/* Indicador de número de imagens */}
                      {(item as any).images.length > 1 && (
                        <div className="absolute bottom-12 right-5 bg-[#fbf0e2] text-voah-teal text-sm px-3 py-1.5 rounded-full flex items-center opacity-90 shadow-md">
                          <Image className="h-4 w-4 mr-1.5" />
                          <span className="font-medium">{currentImageIndices[index] + 1}/{(item as any).images.length}</span>
                        </div>
                      )}
                      
                      {/* Botões de navegação */}
                      {(item as any).images.length > 1 && (
                        <>
                          <button 
                            onClick={(e) => prevImage(index, e)}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all group-hover:bg-[#fbf0e2] group-hover:text-voah-teal group-hover:p-2 group-hover:left-3"
                            aria-label="Imagem anterior"
                          >
                            <ChevronLeft className="h-4 w-4 transition-all group-hover:h-5 group-hover:w-5" />
                          </button>
                          
                          <button 
                            onClick={(e) => nextImage(index, e)}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all group-hover:bg-[#fbf0e2] group-hover:text-voah-teal group-hover:p-2 group-hover:right-3"
                            aria-label="Próxima imagem"
                          >
                            <ChevronRight className="h-4 w-4 transition-all group-hover:h-5 group-hover:w-5" />
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center bg-blue-600 text-white">
                      <div className="text-4xl font-bold">{index + 1}</div>
                      <div className="mt-2 px-4 text-center text-sm">{title}</div>
                      {(item as TransportationInfo).departureLocation && (item as TransportationInfo).arrivalLocation && (
                        <div className="mt-1 text-xs">{(item as TransportationInfo).departureLocation} → {(item as TransportationInfo).arrivalLocation}</div>
                      )}
                      {(item as TransportationInfo).time && (
                        <div className="mt-1 text-xs">Horário: {(item as TransportationInfo).time}</div>
                      )}
                      {(item as TransportationInfo).importantInfo && (
                        <div className="mt-2 mx-4 px-2 py-1 text-xs bg-orange-500/90 rounded-md">
                          <Info className="h-3 w-3 inline-block mr-1" />
                          {(item as TransportationInfo).importantInfo}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            
            // Tratamento para voos
            if (unifiedItem.type === 'flight-departure' || unifiedItem.type === 'flight-arrival') {
              const flight = item as FlightInfo;
              const isArrival = unifiedItem.type === 'flight-arrival';
              const isDeparture = unifiedItem.type === 'flight-departure';
              
              return (
                <div 
                  key={`mobile-item-${unifiedItem.id}`}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    activeIndex === index ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  {/* Sempre usar o cartão informativo para voos, nunca exibir imagens */}
                  <div className="h-full w-full overflow-auto bg-white shadow-md">
                    {/* Flight header com info da companhia aérea */}
                    <div className="bg-voah-teal text-white p-3">
                      <div className="flex flex-col items-center justify-between">
                        <div className="w-full mb-2">
                          <div className="flex items-center justify-center">
                            <PlaneTakeoff className="h-5 w-5 mr-2" />
                            <h3 className="text-lg font-semibold text-center">
                              {isArrival 
                                ? `${flight.arrivalAirport || ''}`
                                : isDeparture
                                ? `${flight.departureAirport || ''}`
                                : `${flight.departureAirport || ''} - ${flight.arrivalAirport || ''}`
                              }
                            </h3>
                          </div>
                          <p className="text-white/80 text-xs text-center mt-1">
                            {flight.airline || 'Companhia aérea não especificada'} 
                            {flight.flightNumber && ` • ${flight.flightNumber}`}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Flight route visualization */}
                    <div className="p-3">
                      <div className="flex flex-col items-center justify-between">
                        {/* Departure information */}
                        <div className="text-center mb-2">
                          <div className="text-xs text-gray-500 mb-1">Partida</div>
                          <div className="text-base font-bold text-voah-teal">{flight.departureAirport}</div>
                          {flight.departureDateTime && (
                            <div className="text-xs text-gray-700">
                              {`${flight.departureDateTime.split('T')[0].split('-').reverse().join('/')}`}
                              <span className="mx-1">•</span>
                              {flight.departureDateTime.split('T')[1]?.substring(0, 5)}
                            </div>
                          )}
                        </div>
                    
                        {/* Flight path visualization */}
                        <div className="flex flex-col items-center justify-center py-1 mb-2">
                          <div className="relative w-24 flex items-center justify-center">
                            <div className="h-0.5 bg-gray-300 w-full absolute"></div>
                            <ArrowRight className="text-voah-teal z-10 h-4 w-4 bg-white rounded-full p-0.5 shadow-sm" />
                          </div>
                          <div className="text-xs text-gray-700 mt-1">
                            {flight.flightDuration || 'Duração não especificada'}
                          </div>
                        </div>
                    
                        {/* Arrival information */}
                        <div className="text-center">
                          <div className="text-xs text-gray-500 mb-1">Chegada</div>
                          <div className="text-base font-bold text-voah-teal">{flight.arrivalAirport}</div>
                          {flight.arrivalDateTime && (
                            <div className="text-xs text-gray-700">
                              {`${flight.arrivalDateTime.split('T')[0].split('-').reverse().join('/')}`}
                              <span className="mx-1">•</span>
                              {flight.arrivalDateTime.split('T')[1]?.substring(0, 5)}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Botão para ver detalhes do voo */}
                      {onViewFlightDetails && (
                        <div className="flex justify-center mt-2">
                          <button
                            onClick={() => {
                              // Usar o flightId de unifiedItem quando disponível
                              const idToUse = unifiedItem.flightId || (item as FlightInfo).id;
                              onViewFlightDetails(idToUse);
                            }}
                            className="text-xs text-voah-teal hover:text-voah-teal-dark flex items-center transition-colors"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Ver detalhes completos
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
            
            // Outros tipos de items (tour, block, restaurant)
            return (
              <div 
                key={`mobile-item-${unifiedItem.id}`}
                className={`absolute inset-0 transition-opacity duration-500 ${
                  activeIndex === index ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
              >
                {hasImages ? (
                  <div className="relative h-full group">
                    <img 
                      src={(item as any).images[currentImageIndices[index]]} 
                      alt={title}
                      className="h-full w-full object-cover"
                    />
                    
                    {/* Title overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white">
                      <p className="font-medium text-sm">{title}</p>
                      {isRestaurant(unifiedItem) && (
                        <>
                          {(item as RestaurantInfo).location && (
                            <p className="text-xs">{(item as RestaurantInfo).location}</p>
                          )}
                          {(item as RestaurantInfo).time && (
                            <p className="text-xs">Horário: {(item as RestaurantInfo).time}</p>
                          )}
                          {(item as RestaurantInfo).notes && (
                            <p className="text-xs mt-1 bg-orange-600/80 p-1 rounded">
                              <Info className="h-3 w-3 inline-block mr-1" />
                              {(item as RestaurantInfo).notes}
                            </p>
                          )}
                        </>
                      )}
                      {isTour(unifiedItem) && (
                        <>
                          {(item as TourInfo).location && (
                            <p className="text-xs">{(item as TourInfo).location}</p>
                          )}
                          {(item as TourInfo).date && (
                            <p className="text-xs">{formatDate((item as TourInfo).date)}</p>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Indicador de número de imagens */}
                    {(item as any).images.length > 1 && (
                      <div className="absolute bottom-12 right-5 bg-[#fbf0e2] text-voah-teal text-sm px-3 py-1.5 rounded-full flex items-center opacity-90 shadow-md">
                        <Image className="h-4 w-4 mr-1.5" />
                        <span className="font-medium">{currentImageIndices[index] + 1}/{(item as any).images.length}</span>
                      </div>
                    )}
                    
                    {/* Botões de navegação */}
                    {(item as any).images.length > 1 && (
                      <>
                        <button 
                          onClick={(e) => prevImage(index, e)}
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all group-hover:bg-[#fbf0e2] group-hover:text-voah-teal group-hover:p-2 group-hover:left-3"
                          aria-label="Imagem anterior"
                        >
                          <ChevronLeft className="h-4 w-4 transition-all group-hover:h-5 group-hover:w-5" />
                        </button>
                        
                        <button 
                          onClick={(e) => nextImage(index, e)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1 transition-all group-hover:bg-[#fbf0e2] group-hover:text-voah-teal group-hover:p-2 group-hover:right-3"
                          aria-label="Próxima imagem"
                        >
                          <ChevronRight className="h-4 w-4 transition-all group-hover:h-5 group-hover:w-5" />
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="h-full w-full flex flex-col items-center justify-center bg-voah-teal text-white">
                    <div className="text-4xl font-bold">{index + 1}</div>
                    <div className="mt-2 px-4 text-center text-sm">{title}</div>
                    {/* Usar um IIFE com type guards para corrigir o erro de linting */}
                    {(() => {
                      if (isRestaurant(unifiedItem)) {
                        const restaurantItem = item as RestaurantInfo;
                        return (
                          <>
                            {restaurantItem.location && (
                              <div className="mt-1 text-xs">{restaurantItem.location}</div>
                            )}
                            {restaurantItem.time && (
                              <div className="mt-1 text-xs">Horário: {restaurantItem.time}</div>
                            )}
                            {restaurantItem.notes && (
                              <div className="mt-2 mx-4 px-2 py-1 text-xs bg-orange-500/90 rounded-md">
                                <Info className="h-3 w-3 inline-block mr-1" />
                                {restaurantItem.notes}
                              </div>
                            )}
                          </>
                        );
                      }
                      
                      if (isTransportation(unifiedItem)) {
                        const transportationItem = item as TransportationInfo;
                        return (
                          <>
                            {transportationItem.departureLocation && transportationItem.arrivalLocation && (
                              <div className="mt-1 text-xs">{transportationItem.departureLocation} → {transportationItem.arrivalLocation}</div>
                            )}
                            {transportationItem.time && (
                              <div className="mt-1 text-xs">Horário: {transportationItem.time}</div>
                            )}
                            {transportationItem.importantInfo && (
                              <div className="mt-2 mx-4 px-2 py-1 text-xs bg-orange-500/90 rounded-md">
                                <Info className="h-3 w-3 inline-block mr-1" />
                                {transportationItem.importantInfo}
                              </div>
                            )}
                          </>
                        );
                      }
                      
                      if (isAccommodation(unifiedItem) || isAccommodationCheckin(unifiedItem) || isAccommodationCheckout(unifiedItem)) {
                        const accommodationItem = item as AccommodationInfo;
                        // Adicionar prefixo no título baseado no tipo
                        const displayTitle = isAccommodationCheckin(unifiedItem) 
                          ? `Check-in: ${accommodationItem.name}` 
                          : isAccommodationCheckout(unifiedItem)
                            ? `Check-out: ${accommodationItem.name}`
                            : accommodationItem.name;

                        return (
                          <>
                            {/* Exibir o título com prefixo quando aplicável */}
                            <div className="mt-1 font-medium text-xs">{displayTitle}</div>
                            {accommodationItem.location && (
                              <div className="mt-1 text-xs">{accommodationItem.location}</div>
                            )}
                            {(accommodationItem.checkInTime || accommodationItem.checkOutTime) && (
                              <div className="mt-1 text-xs">
                                {accommodationItem.checkInTime && <>Check-in: {accommodationItem.checkInTime}</>}
                                {accommodationItem.checkInTime && accommodationItem.checkOutTime && <> | </>}
                                {accommodationItem.checkOutTime && <>Check-out: {accommodationItem.checkOutTime}</>}
                              </div>
                            )}
                          </>
                        );
                      }
                      
                      return null;
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Modal do mapa em tela cheia */}
        {mapModalOpen && (
          <MapModal 
            isOpen={mapModalOpen} 
            onClose={() => setMapModalOpen(false)} 
            blocks={blocks}
            tours={tours}
            transportations={transportations}
            restaurants={restaurants}
            accommodations={accommodations}
            flights={flights}
            flightEvents={flightEvents}
            accommodationEvents={accommodationEvents}
            unifiedItems={unifiedItems}
          />
        )}
      </div>
    </div>
  );
} 